import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, signInAnonymously, signInWithCustomToken, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, collection, onSnapshot, query, where, writeBatch, serverTimestamp, getDocs, deleteDoc, addDoc, orderBy, arrayUnion, increment, runTransaction, collectionGroup } from 'firebase/firestore';

// --- Brand Colors ---
const brandColors = {
  federalBlue: '#150e60',
  palatinateBlue: '#473dc6',
  wisteria: '#caa3d6',
  columbiaBlue: '#cfe6ff',
  jet: '#2b2b2b',
  isabelline: '#f1eae7',
  // Lighter shade for hovers
  palatinateBlueLight: '#6A61D8',
};

// --- Avatars ---
const avatars = [
    { emoji: '🛡️', color: 'ec4899' },
    { emoji: '🔐', color: '8b5cf6' },
    { emoji: '🐛', color: '10b981' },
    { emoji: '🤖', color: 'f59e0b' },
    { emoji: '🧠', color: '3b82f6' },
    { emoji: '🦊', color: 'f97316' },
    { emoji: '🦄', color: 'd946ef' },
    { emoji: '👩🏾‍💻', color: '22d3ee' },
];

// Helper to find avatar object from URL
const findAvatarFromUrl = (url) => {
    if (!url) return null;
    const match = url.match(/text=(.*?)$/);
    if (!match) return null;
    const emoji = decodeURIComponent(match[1]);
    return avatars.find(a => a.emoji === emoji) || null;
};

// --- Helper Function for Simple Markdown ---
const parseSimpleMarkdown = (text = '') => {
    if (!text) return '';
    // This function handles basic formatting. It is NOT a full markdown parser.
    // For security, it's important this doesn't render arbitrary HTML.
    return text
        .replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color:${brandColors.wisteria}; text-decoration: underline;">$1</a>`) // Hyperlinks
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
        .replace(/\*(.*?)\*/g, '<em>$1</em>')         // Italic
        .replace(/\n/g, '<br />');                  // Newlines
};

// --- REMOVED marqueeStyles ---
// const marqueeStyles = `
//   @keyframes marquee {
// ...
//   }
// `;


// --- SVG Icons ---
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-1 text-yellow-400"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>;
const CheckCircleIcon = ({className}) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;
const AlertTriangleIcon = ({className}) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
const InfoIcon = ({className}) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
const FeedbackCheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;
const FeedbackXIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>;
const TrophyIcon = ({ size = 24, className = "" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={brandColors.isabelline} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`inline-block ${className}`}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.87 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98-.97 1.21C16.13 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>;
const PlayIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" viewBox="0 0 20 20" fill={brandColors.wisteria}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>;
const ChevronDown = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>;
const ChevronLeft = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>;
const ChevronRight = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>;
const Spinner = () => <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" style={{borderColor: brandColors.palatinateBlue}}></div></div>;
const SparkleIcon = ({ size = 24, className = "" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{color: brandColors.wisteria}}><path d="M12 2L9 9l-7 3 7 3 2 7 2-7 7-3-7-3L12 2z"/></svg>;
const BadgeIcon = ({ size = 24, className = "" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3.85 8.62a4 4 0 0 1 4.78-4.78l1.21 1.21a1 1 0 0 0 1.41 0l1.11-1.11a1 1 0 0 1 1.41 0l1.11 1.11a1 1 0 0 0 1.41 0l1.21-1.21a4 4 0 0 1 4.78 4.78l-1.21 1.21a1 1 0 0 0 0 1.41l1.11 1.11a1 1 0 0 1 0 1.41l-1.11 1.11a1 1 0 0 0 0 1.41l-1.21 1.21a4 4 0 0 1-4.78 4.78l-1.21-1.21a1 1 0 0 0-1.41 0l-1.11 1.11a1 1 0 0 1-1.41 0l-1.11-1.11a1 1 0 0 0-1.41 0l-1.21 1.21a4 4 0 0 1-4.78-4.78l1.21-1.21a1 1 0 0 0 0-1.41l-1.11-1.11a1 1 0 0 1 0 1.41l1.11-1.11a1 1 0 0 0 0-1.41z"/></svg>;
const UsersIcon = ({ size = 24, className = "" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{color: brandColors.wisteria}}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const ShieldIcon = ({ size = 24, className = "" }) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{color: brandColors.wisteria}}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const TargetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>;
const CommunityIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const CreateHerFestLogo = ({className}) => <img src="https://i.imgur.com/eQehK55.png" alt="CreateHER Fest Logo" className={className} />;
const HourglassIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-1"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>;
const HelpIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
const CalendarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>;


// --- Components ---
const Header = ({ user, userData, onLogout, onNavigate, onJoin, hasUnviewedBadges, hasUnviewedBonusContent }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const userIsAnonymous = !user || user.isAnonymous;
    const avatarObject = userData ? findAvatarFromUrl(userData.avatar) : null;
    const navLinkClasses = "relative text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium";
    const mobileNavLinkClasses = "block text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-base font-medium";
    const glowingLinkStyle = {
        boxShadow: `0 0 15px ${brandColors.wisteria}80`,
    };
    const dropdownRef = useRef(null);

    const hasNotification = hasUnviewedBadges || hasUnviewedBonusContent;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsAccountMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleNav = (page) => {
        onNavigate(page);
        setIsMenuOpen(false);
        setIsAccountMenuOpen(false);
    }

    return (
      <header className="backdrop-blur-sm sticky top-0 z-50" style={{backgroundColor: 'rgba(21, 14, 96, 0.5)'}}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 cursor-pointer" onClick={() => handleNav('dashboard')}>
                  <h1 className="text-2xl font-bold" style={{color: brandColors.wisteria}}>CybHER Challenge</h1>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <button onClick={() => handleNav('dashboard')} className={navLinkClasses}>Home</button>
                  <button onClick={() => handleNav('leaderboard')} className={navLinkClasses}>Leaderboard</button>
                  
                  {/* --- NEW BUTTON ADDED HERE --- */}
                  <a
                    href="https://newsletter.createherfest.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={navLinkClasses}
                    style={{backgroundColor: brandColors.palatinateBlueLight, color: brandColors.isabelline}} // Make it stand out
                  >
                    Newsletter
                  </a>
                  {/* --- END OF NEW BUTTON --- */}

                  {!userIsAnonymous && (
                    <div className="relative" ref={dropdownRef}>
                        <button onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)} className={navLinkClasses} style={(hasNotification) ? glowingLinkStyle : {}}>
                            My Account
                            {(hasNotification) && <span className="absolute top-0 right-0 block h-2 w-2 rounded-full ring-2 ring-gray-800 bg-red-500"></span>}
                        </button>
                        {isAccountMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-[#211a6e] rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-20">
                                <button onClick={() => handleNav('rewards')} className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700">My Badges</button>
                                <button onClick={() => handleNav('bonus')} className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700">Bonus Points</button>
                                <button onClick={() => handleNav('profile')} className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700">My Profile</button>
                            </div>
                        )}
                    </div>
                  )}
                  {userData?.isAdmin && <button onClick={() => handleNav('admin')} className={navLinkClasses}>Admin</button>}
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              {!userIsAnonymous && userData ? (
                <div className="ml-4 flex items-center md:ml-6">
                  <span className="text-gray-300 mr-3">Welcome, {userData.displayName}!</span>
                   {avatarObject ? (
                        <span className="h-8 w-8 rounded-full flex items-center justify-center text-xl" style={{backgroundColor: `#${avatarObject.color}`}}>{avatarObject.emoji}</span>
                    ) : (
                        <img className="h-8 w-8 rounded-full" src={userData.avatar} alt="Avatar" />
                    )}
                  <button onClick={onLogout} style={{backgroundColor: brandColors.palatinateBlue}} className="ml-4 hover:opacity-80 text-white px-3 py-2 rounded-md text-sm font-medium">Logout</button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                     <button onClick={() => onJoin('login')} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm font-medium">Login</button>
                     <button onClick={() => onJoin('register')} style={{backgroundColor: brandColors.palatinateBlue}} className="hover:opacity-80 text-white px-3 py-2 rounded-md text-sm font-medium">Register</button>
                </div>
              )}
            </div>
            <div className="-mr-2 flex md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} type="button" className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white" aria-controls="mobile-menu" aria-expanded="false">
                <span className="sr-only">Open main menu</span>
                {!isMenuOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </nav>
        {isMenuOpen && (
          <div className="md:hidden" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button onClick={() => handleNav('dashboard')} className={mobileNavLinkClasses}>Home</button>
              <button onClick={() => handleNav('leaderboard')} className={mobileNavLinkClasses}>Leaderboard</button>
              
              {/* --- NEW BUTTON ADDED TO MOBILE MENU --- */}
              <a
                href="https://newsletter.createherfest.com"
                target="_blank"
                rel="noopener noreferrer"
                className={mobileNavLinkClasses}
                style={{backgroundColor: brandColors.palatinateBlueLight, color: brandColors.isabelline}} // Make it stand out
              >
                Newsletter
              </a>
              {/* --- END OF NEW MOBILE BUTTON --- */}

               {!userIsAnonymous && (
                 <>
                    <button onClick={() => handleNav('rewards')} className={mobileNavLinkClasses} style={hasUnviewedBadges ? glowingLinkStyle : {}}>
                        My Badges
                        {hasUnviewedBadges && <span className="inline-block ml-2 h-2 w-2 rounded-full bg-red-500"></span>}
                    </button>
                    <button onClick={() => handleNav('bonus')} className={mobileNavLinkClasses} style={hasUnviewedBonusContent ? glowingLinkStyle : {}}>
                        Bonus Points
                        {hasUnviewedBonusContent && <span className="inline-block ml-2 h-2 w-2 rounded-full bg-red-500"></span>}
                    </button>
                    <button onClick={() => handleNav('profile')} className={mobileNavLinkClasses}>My Profile</button>
                 </>
                )}
              {userData?.isAdmin && <button onClick={() => handleNav('admin')} className={mobileNavLinkClasses}>Admin</button>}
              <div className="border-t border-gray-700 pt-4 mt-4">
                  {!userIsAnonymous && userData ? (
                     <div className="flex items-center px-3">
                        <div className="flex-shrink-0">
                            {avatarObject ? (
                                <span className="h-10 w-10 rounded-full flex items-center justify-center text-2xl" style={{backgroundColor: `#${avatarObject.color}`}}>{avatarObject.emoji}</span>
                            ) : (
                                <img className="h-10 w-10 rounded-full" src={userData.avatar} alt="Avatar" />
                            )}
                        </div>
                        <div className="ml-3">
                            <div className="text-base font-medium leading-none text-white">{userData.displayName}</div>
                        </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                        <button onClick={() => onJoin('login')} className={`${mobileNavLinkClasses} w-full text-left`}>Login</button>
                        <button onClick={() => onJoin('register')} className={`${mobileNavLinkClasses} w-full text-left`}>Register</button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </header>
    );
};

// --- Updated Component: ScoreBreakdown (Segment A) ---
const ScoreBreakdown = ({ 
    player, 
    maxChallengePoints, 
    maxBonusPoints, 
    maxBasePoints, 
    challengeBreakdown, 
    bonusBreakdown      
}) => {
    // Uses the stored breakdown from user doc for fallback totals
    const breakdown = player.pointsBreakdown || { base: 0, challenges: 0, bonus: 0 };
    const totalMaxScore = maxBasePoints + maxChallengePoints + maxBonusPoints;

    // --- FIX: This line ensures the 10 points display correctly ---
    // If the player is on the leaderboard (opted in), always show the maxBasePoints,
    // otherwise, fall back to what's in the DB (which is likely 0).
    const basePointsDisplay = player.leaderboardOptIn ? maxBasePoints : breakdown.base;

    // Use the detailed breakdown lists to calculate accurate totals for display headers
    const totalChallengePoints = challengeBreakdown.reduce((sum, item) => sum + (item.score || 0), 0);
    const totalBonusPoints = bonusBreakdown.reduce((sum, item) => sum + (item.points || 0), 0);
    
    return (
        <div className="text-white p-4">
            <h4 className="font-bold text-lg mb-4" style={{ color: brandColors.wisteria }}>Score Breakdown</h4>
            <ul className="space-y-3">
                <li className="flex justify-between items-center text-sm">
                    <span className="text-gray-300">Leaderboard Bonus</span>
                    {/* This line now uses the corrected display variable */}
                    <span className="font-medium">{basePointsDisplay} / {maxBasePoints} pts</span>
                </li>
                
                {/* DETAILED CHALLENGE BREAKDOWN */}
                <li className="font-bold text-base mt-4 pt-2 border-t border-gray-600 flex justify-between items-center">
                    <span>Challenge Points</span>
                    <span className="font-medium">{totalChallengePoints} / {maxChallengePoints} pts</span>
                </li>
                {/* --- Reverting temporary test back to max-h-56 --- */}
                <div className="pl-4 space-y-1 max-h-56 overflow-y-auto">
                    {challengeBreakdown.length > 0 ? challengeBreakdown.map((item) => (
                        <li key={item.challengeId} className="flex justify-between items-center text-xs text-gray-400">
                            <span className="truncate pr-2">{item.challengeTitle || `Challenge ID: ${item.challengeId ? item.challengeId.substring(0, 8) : 'N/A'}...`}</span>
                            <span className="font-medium flex-shrink-0" style={{ color: item.score > 0 ? brandColors.wisteria : 'inherit' }}>{item.score} pts</span>
                        </li>
                    )) : <li className="text-xs text-gray-500">No challenge scores recorded.</li>}
                </div>
                
                {/* DETAILED BONUS BREAKDOWN */}
                <li className="font-bold text-base mt-4 pt-2 border-t border-gray-600 flex justify-between items-center">
                    <span>Smart Security Actions</span>
                    <span className="font-medium">{totalBonusPoints} / {maxBonusPoints} pts</span>
                </li>
                {/* --- Reverting temporary test back to max-h-56 --- */}
                <div className="pl-4 space-y-1 max-h-56 overflow-y-auto">
                    {bonusBreakdown.length > 0 ? bonusBreakdown.map((item, index) => (
                        <li key={item.id || index} className="flex justify-between items-center text-xs text-gray-400">
                            <span className="truncate pr-2">{item.name}</span>
                            <span className="font-medium flex-shrink-0" style={{ color: item.points > 0 ? brandColors.wisteria : 'inherit' }}>{item.points} pts</span>
                        </li>
                    )) : <li className="text-xs text-gray-500">No approved bonus points.</li>}
                </div>
                
                {/* GRAND TOTAL */}
                <li className="flex justify-between items-center font-bold border-t border-gray-600 pt-3 mt-3">
                    <span>Total Score</span>
                    {/* Display stored totalPoints from player document */}
                    <span>{player.totalPoints || 0} / {totalMaxScore} pts</span>
                </li>
            </ul>
        </div>
    );
};


// --- Updated Component: LeaderboardPage (Segment B) ---
const LeaderboardPage = ({ leaderboardData, currentUserData, maxChallengePoints, maxBonusPoints, maxBasePoints, allChallengeSubmissionsByUser, allBonusSubmissionsByUser, challenges }) => {
    const [expandedPlayerId, setExpandedPlayerId] = useState(null);

    // Map challenge IDs to titles for cleaner display in the breakdown
    const challengeTitleMap = useMemo(() => {
        return challenges.reduce((acc, c) => {
            acc[c.id] = c.title;
            return acc;
        }, {});
    }, [challenges]);
    
    // Core function to retrieve the DETAILED breakdown from the pre-processed public collections.
    const calculateDetailedBreakdown = (playerId) => {
        
        // 1. CHALLENGE BREAKDOWN: Fetch the specific user's breakdown
        const breakdownData = allChallengeSubmissionsByUser[playerId] || [];
        let challengeBreakdown = [];

        // Check if we have a flat list (Admin case: uses scoreAwarded)
        const isFlatAttemptList = breakdownData.length > 0 && (
            breakdownData[0].scoreAwarded !== undefined || breakdownData[0].score !== undefined
        );

        if (isFlatAttemptList) {
             const attempts = breakdownData;
             const bestScoresMap = new Map();
             attempts.forEach(att => {
                if (!att.challengeId) return; 
                // CRITICAL FIX: Use the highest available score from EITHER scoreAwarded or score
                const scoreAwarded = att.scoreAwarded !== undefined ? att.scoreAwarded : (att.score || 0);
                const currentBest = bestScoresMap.get(att.challengeId)?.score || 0;
                
                // --- THIS IS THE FIX: Changed > to >= to include 0-score challenges ---
                if (scoreAwarded >= currentBest) {
                    const title = challengeTitleMap[att.challengeId] || `Challenge ID: ${att.challengeId.substring(0, 8)}...`;
                    bestScoresMap.set(att.challengeId, { 
                        challengeId: att.challengeId,
                        score: scoreAwarded, 
                        challengeTitle: title
                    });
                }
            });
            challengeBreakdown = Array.from(bestScoresMap.values()).sort((a, b) => (a.challengeTitle || "").localeCompare(b.challengeTitle || ""));
        } else {
            // This is a PRE-CALCULATED REPORT (non-admin), just use it
            challengeBreakdown = breakdownData.sort((a, b) => (a.challengeTitle || "").localeCompare(b.challengeTitle || ""));
        } 


        // 2. BONUS BREAKDOWN: Fetch and filter the specific user's bonus submissions from the public collection
        const userBonusSubmissions = allBonusSubmissionsByUser[playerId] || [];

        const bonusBreakdown = userBonusSubmissions
            // FIX: Ensure status check is case-insensitive
            .filter(sub => sub.status && sub.status.toLowerCase() === 'approved')
            .map(sub => ({ 
                name: sub.actionName, 
                points: sub.pointsAwarded || 0,
                id: sub.id // Use ID for list key
            }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        return { challengeBreakdown, bonusBreakdown };
    };


    return (
        <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8"><h1 className="text-4xl font-bold" style={{color: brandColors.wisteria}}>Leaderboard</h1><p className="text-gray-300 mt-2">See who's at the top of their game. Keep solving to climb the ranks!</p></div>
            {leaderboardData.length > 0 ? (
                <div className="bg-[#211a6e] rounded-lg shadow-lg overflow-hidden border border-gray-700">
                    <div className="overflow-x-auto">
                        <table className="min-w-full"><thead className="bg-black/20"><tr>
                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-16">Rank</th>
                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Player</th>
                            <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Score</th>
                            <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider w-12"></th>
                        </tr></thead>
                            <tbody className="divide-y divide-gray-700">{leaderboardData.map((player, index) => {
                                const playerAvatarObject = findAvatarFromUrl(player.avatar);
                                const isExpanded = expandedPlayerId === player.id;
                                
                                // Calculate breakdown data only for the expanded row for performance
                                const breakdownProps = isExpanded 
                                    ? calculateDetailedBreakdown(player.id) 
                                    : { challengeBreakdown: [], bonusBreakdown: [] };

                                return (
                                    <React.Fragment key={player.id}>
                                        <tr 
                                            className={`cursor-pointer ${player.id === currentUserData?.id ? 'bg-[#473dc6]/20' : ''} hover:bg-black/20`}
                                            onClick={() => setExpandedPlayerId(isExpanded ? null : player.id)}
                                        >
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-lg font-bold">{index + 1}</td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        {playerAvatarObject ? (
                                                            <span className="h-10 w-10 rounded-full flex items-center justify-center text-2xl" style={{backgroundColor: `#${playerAvatarObject.color}`}}>{playerAvatarObject.emoji}</span>
                                                        ) : (
                                                            <img className="h-10 w-10 rounded-full" src={player.avatar} alt="" />
                                                        )}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-white">{player.displayName}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-lg font-bold" style={{color: brandColors.wisteria}}>{player.totalPoints || 0}</td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                                                <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                                    <ChevronDown />
                                                </span>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-[#150e60]">
                                                <td colSpan="4" className="px-4 sm:px-6 py-4">
                                                    <ScoreBreakdown
                                                        player={player}
                                                        maxChallengePoints={maxChallengePoints}
                                                        maxBonusPoints={maxBonusPoints}
                                                        maxBasePoints={maxBasePoints}
                                                        {...breakdownProps}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}</tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="text-center py-16 bg-[#211a6e] rounded-lg border border-gray-700">
                    <TrophyIcon size={48} className="mx-auto" />
                    <h2 className="mt-4 text-2xl font-bold text-white">The Leaderboard is Live!</h2>
                    <p className="text-gray-300">Be the first to solve a challenge and claim the top spot.</p>
                </div>
            )}
        </div>
    );
};


// --- NEW HOMEPAGE COMPONENT ---
const HomePage = ({ user, userData, challenges, onSelectChallenge, loading, broadcasts, allUsersCount, onAuthModalOpen, onNavigate, showMessage }) => {
    const userIsAnonymous = !user || user.isAnonymous;
    const userAvatarObject = userData ? findAvatarFromUrl(userData.avatar) : null;
    
    // Logic for 'isUnlocked'
    const isUnlocked = (challenge) => {
        if (userData?.isAdmin) return true;
        return challenge.unlockDate ? new Date() >= new Date(challenge.unlockDate) : true;
    };
    
    // --- UPDATED LiveEvents component (cleaner UI) ---
    const LiveEvents = ({ events = [] }) => {
        // We'll just show the *latest* broadcast, not all of them.
        const latestEvent = events?.[0];

        if (!latestEvent) return null;
        
        return (
            // Changed from red marquee to purple info banner
            <div className="max-w-4xl mx-auto mb-8 bg-purple-500/20 text-purple-200 border border-purple-400/30 rounded-lg p-4 overflow-hidden shadow-lg">
                <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                         <InfoIcon className="h-6 w-6 text-purple-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white">{latestEvent.title}</h3>
                        <p className="text-sm mt-1">{latestEvent.message}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-4xl mx-auto">
            
            {/* --- MOVED BROADCAST HERE --- */}
            {/* Now appears at the top for BOTH logged-in and logged-out users */}
            <LiveEvents events={broadcasts} />

            {!userIsAnonymous && userData ? (
                <div className="text-center max-w-4xl mx-auto pt-8 pb-16 px-4">
                    {userAvatarObject ? (
                        <span
                            className="h-24 w-24 rounded-full mx-auto mb-4 border-4 flex items-center justify-center text-5xl"
                            style={{
                                backgroundColor: `#${userAvatarObject.color}`,
                                borderColor: brandColors.wisteria,
                            }}
                        >
                            {userAvatarObject.emoji}
                        </span>
                    ) : (
                        <img src={userData.avatar} alt="Your avatar" className="h-24 w-24 rounded-full mx-auto mb-4 border-4" style={{ borderColor: brandColors.wisteria }} />
                    )}
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        Welcome back, <span style={{ color: brandColors.wisteria }}>{userData.displayName}!</span>
                    </h1>
                    <p className="text-lg text-gray-300">Ready to tackle the next challenge?</p>
                    <div className="mt-8 flex justify-center items-center gap-8">
                        <div className="text-center">
                            <p className="text-3xl font-bold" style={{ color: brandColors.wisteria }}>{userData.totalPoints || 0}</p>
                            <p className="text-gray-400">Total Points</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl font-bold" style={{ color: brandColors.wisteria }}>{(userData.badges || []).length}</p>
                            <p className="text-gray-400">Badges Earned</p>
                        </div>
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
                        <button
                            onClick={() => document.getElementById('challenges-section').scrollIntoView({ behavior: 'smooth' })}
                            style={{ backgroundColor: brandColors.palatinateBlue, color: brandColors.isabelline }}
                            className="w-full sm:w-auto hover:opacity-80 font-bold py-3 px-8 rounded-lg transition-transform transform hover:scale-105 text-lg"
                        >
                            View Challenges
                        </button>
                        <button
                            onClick={() => onNavigate('bonus')}
                            style={{ borderColor: brandColors.wisteria, color: brandColors.wisteria }}
                            className="w-full sm:w-auto bg-transparent border-2 font-bold py-3 px-8 rounded-lg transition-colors hover:bg-[#caa3d620]"
                        >
                            Bonus Points
                        </button>
                    </div>
                </div>
            ) : (
                <div className="text-center max-w-4xl mx-auto pt-8 pb-16 px-4">
                    <ShieldIcon size={50} className="mx-auto mb-4" />
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Cyb<span style={{ color: brandColors.wisteria }}>HER</span> Challenge
                    </h1>
                    <div className="mt-8 text-lg text-gray-300 max-w-3xl mx-auto">
                        <p className="mb-4">
                            All month long, CreateHER Fest is turning Cybersecurity Awareness Month into 31 days of hands-on, interactive challenges that build your digital defense skills.
                        </p>
                        <p>
                            Builders, innovators, and creatives are invited to test their cyberfluency, tackle puzzles drawn from real-world risks in AI, blockchain, and AR/VR; and win big prizes! Whether you’re a cybersecurity pro or just starting your tech journey, this challenge is for you.
                        </p>
                    </div>
                    <div className="mt-10 flex justify-center items-center">
                        <button
                            onClick={() => onAuthModalOpen('register')}
                            style={{ backgroundColor: brandColors.palatinateBlue, color: brandColors.isabelline }}
                            className="hover:opacity-80 font-bold py-3 px-8 rounded-lg transition-transform transform hover:scale-105 text-lg"
                        >
                            Start Your Journey
                        </button>
                    </div>
                </div>
            )}

            {/* --- REMOVED BROADCAST FROM HERE --- */}
            {/* <LiveEvents events={broadcasts} /> */}

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-white max-w-4xl mx-auto">
                <div className="text-center">
                    <UsersIcon size={32} className="mx-auto mb-2"/>
                    <p className="text-2xl font-bold">{allUsersCount}</p>
                    <p className="text-gray-400">Participants</p>
                </div>
                <div className="text-center">
                    <BadgeIcon size={32} className="mx-auto mb-2" style={{ color: brandColors.wisteria }} />
                    <p className="text-2xl font-bold">{challenges.length}+</p>
                    <p className="text-gray-400">Security Challenges</p>
                </div>
                <div className="text-center">
                    <SparkleIcon size={32} className="mx-auto mb-2"/>
                    <p className="text-2xl font-bold">600+</p>
                    <p className="text-gray-400">Points Available</p>
                </div>
            </div>

            {userIsAnonymous && (
                <div className="max-w-5xl mx-auto py-16 px-4 bg-[#211a6e] rounded-2xl border border-gray-700 mt-24">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white">Why Join the CybHER Challenge?</h2>
                        <p className="mt-2 text-lg text-gray-300">
                            Our platform offers a comprehensive cybersecurity learning experience designed specifically for<br />
                            Cybersecurity Awareness Month.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-4 gap-8 text-center">
                        <WhyJoinCard
                            icon={<TargetIcon style={{ color: brandColors.wisteria }} />}
                            title="Real-world Scenarios"
                            description="Practice with authentic cybersecurity challenges based on real threats."
                        />
                        <WhyJoinCard
                            icon={<TrophyIcon size={32} />}
                            title="Win Scholarships"
                            description="Climb the leaderboard for a chance to win top prizes, including SANS course scholarships."
                        />
                        <WhyJoinCard
                            icon={<BadgeIcon size={32} style={{ color: brandColors.wisteria }} />}
                            title="Digital Badges"
                            description="Earn recognition for your cybersecurity knowledge and skills."
                        />
                        <WhyJoinCard
                            icon={<CommunityIcon style={{ color: brandColors.wisteria }} />}
                            title="Community Learning"
                            description="Join a supportive community of women in cybersecurity."
                        />
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto my-24">
                <InfoTile icon={<PlayIcon />} title="How to Play">
                    <ul className="space-y-3 text-gray-300">
                        <li className="flex items-start">
                            <span style={{ color: brandColors.wisteria }} className="font-bold mr-2">1.</span> <span><strong>Join the Challenge:</strong> Sign up to get started.</span>
                        </li>
                        <li className="flex items-start">
                            <span style={{ color: brandColors.wisteria }} className="font-bold mr-2">2.</span> <span><strong>Solve Puzzles:</strong> A new challenge unlocks every few days. Each expands your cyber knowledge, from data leaks to a Halloween grand finale!</span>
                        </li>
                        <li className="flex items-start">
                            <span style={{ color: brandColors.wisteria }} className="font-bold mr-2">3.</span> <span><strong>Submit Answers:</strong> Review the artifacts and submit your solution via the form on each challenge page.</span>
                        </li>
                        <li className="flex items-start">
                            <span style={{ color: brandColors.wisteria }} className="font-bold mr-2">4.</span> <span><strong>Don't Share Spoilers:</strong> We want every player to stay curious! Keep solutions private.</span>
                        </li>
                    </ul>
                </InfoTile>
                <InfoTile icon={<TrophyIcon />} title="Scoring & Prizes">
                    <div className="flex flex-col h-full">
                        <p className="text-gray-300 flex-grow">
                            Each challenge is worth up to 50 points (finale is 100!). Accuracy wins! Thanks to generous support from SANS, two grand winners will receive a scholarship for one SANS OnDemand cybersecurity course.
                        </p>
                        <a
                            href="https://www.sans.org/cyber-security-courses"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ backgroundColor: brandColors.columbiaBlue, color: brandColors.federalBlue }}
                            className="block w-full text-center font-bold py-3 px-4 rounded-lg transition-opacity hover:opacity-90 mt-6"
                        >
                            Win a SANS OnDemand Scholarship!
                        </a>
                    </div>
                </InfoTile>
            </div>

            <div id="challenges-section" className="text-center mt-24 mb-12">
                <h2 className="text-3xl font-bold">The Challenges</h2>
                <p className="text-gray-400 mt-1">A new challenge unlocks every few days. Select one to begin!</p>
            </div>
            {loading ? (
                <Spinner />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {challenges.map((challenge) => {
                        const unlocked = isUnlocked(challenge);
                        const isNext =
                            !unlocked &&
                            challenges
                                .filter((c) => c.unlockDate && new Date() < new Date(c.unlockDate))
                                .sort((a, b) => new Date(a.unlockDate) - new Date(b.unlockDate))[0]?.id === challenge.id;
                        return (
                            <div
                                key={challenge.id}
                                onClick={() => unlocked && onSelectChallenge(challenge.id)} // FIX: only call onSelectChallenge if unlocked
                                className={`bg-[#211a6e] rounded-lg shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-transparent hover:border-[#473dc6] ${
                                    unlocked ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'
                                } flex flex-col hover:shadow-xl hover:shadow-[#473dc6]/20`} 
                            >
                                <div className="p-6 flex flex-col flex-grow"> 
                                    {/* --- MODIFIED BLOCK --- */}
                                    <div className="h-28"> {/* h-28 = 7rem */}
                                        
                                        {/* Pill is now first, wrapped to be on the right */}
                                        <div className="flex justify-end">
                                            <div
                                                className={`flex-shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${ 
                                                    challenge.difficulty === 'Beginner'
                                                        ? 'bg-green-500/20 text-green-300'
                                                        : challenge.difficulty === 'Intermediate'
                                                        ? 'bg-yellow-500/20 text-yellow-300'
                                                        : 'bg-red-500/20 text-red-300'
                                                }`}
                                            >
                                                {challenge.difficulty}
                                            </div>
                                        </div>
                        
                                        {/* Title is second, with a top margin for spacing */}
                                        <h2 className="text-xl font-bold mt-2 text-white">{challenge.title}</h2>
                                        
                                    </div>
                                    {/* --- END MODIFIED BLOCK --- */}
                                    <p className="text-gray-400 text-sm mb-4 flex-grow"> 
                                        {challenge.scenario.substring(0, 100)}...
                                    </p>
                                    <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-700"> 
                                        <span className="text-lg font-bold" style={{ color: brandColors.wisteria }}>
                                            {challenge.points} pts
                                        </span>
                                        {unlocked ? (
                                            <span className="text-green-400 text-sm font-bold flex items-center">
                                                <CheckCircleIcon className="h-5 w-5 mr-1" /> UNLOCKED
                                            </span>
                                        ) : isNext ? (
                                            <span className="text-blue-400 text-sm font-bold">
                                                UP NEXT - {new Date(challenge.unlockDate).toLocaleDateString()}
                                            </span>
                                        ) : (
                                            <span className="text-gray-500 text-sm font-bold flex items-center">
                                                <LockIcon /> {new Date(challenge.unlockDate).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="max-w-4xl mx-auto mt-16 space-y-4">
              <AccordionItem title="Code of Conduct & Rules">
                <p className="text-gray-300">CreateHer Fest is dedicated to a harassment-free experience for everyone. All participants are expected to be respectful and supportive. No sharing of answers or "flags." Play fair, have fun, and lift each other up!</p>
              </AccordionItem>
               <AccordionItem title="Submission & FAQ">
                <p className="text-gray-300"><strong>Can I revise my answer?</strong> Yes! Submit again—your last entry per challenge is what counts. <br/> <strong>How are winners chosen?</strong> Scoring is based on accuracy per the admin sheet; tiebreakers go to the earliest correct entries. <br/> <strong>Do I need to be technical?</strong> No! All challenges support creative logic and critical thinking. <br/> <strong>How will I know if I win?</strong> Winners are notified by email and announced at our closing virtual celebration. <br/><strong>Need help?</strong> Email our support team at <a href="mailto:adriann@createherfest.com" style={{color: brandColors.wisteria}} className="underline">adriann@createherfest.com</a>.</p>
              </AccordionItem>
              <AccordionItem title="Learn more about CreateHER Fest">
                <p className="text-gray-300 mb-4">CreateHER Fest is a registered 501(c)(3) nonprofit advancing the next generation of builders, innovators, and leaders in AI, blockchain, AR/VR, cybersecurity, and emerging technologies. Through our annual programming and campaigns, we provide accessible learning, global community engagement, and innovation challenges that equip participants with practical skills while accelerating real-world solutions.</p>
                <p className="text-gray-300 mb-4">Our work is designed to strengthen the future workforce, expand the pipeline of diverse technical talent, and showcase responsible innovation at scale. By collaborating with partners, sponsors, and donors, we align community impact with strategic objectives—whether that’s developing talent, driving thought leadership, or demonstrating year-end results in security and innovation.</p>
                <p className="text-gray-300">👉 To learn more, visit <a href="https://createherfest.com" target="_blank" rel="noopener noreferrer" style={{color: brandColors.wisteria}} className="underline">createherfest.com</a> or contact us directly at <a href="mailto:info@createherfest.org" style={{color: brandColors.wisteria}} className="underline">info@createherfest.org</a>.</p>
              </AccordionItem>
              <AccordionItem title="Accessibility & Inclusion">
                  <p className="text-gray-300">Great security depends on diverse perspectives. Our challenges are crafted to be engaging for all skill levels and learning styles, opening the door for more builders to thrive.</p>
              </AccordionItem>
            </div>
            
            <FinalCTA onJoin={onAuthModalOpen} user={user} userData={userData} onNavigate={onNavigate} />
        </div>
    );
};


const JoinLoginModule = ({
    onLogin,
    onRegister,
    onForgotPassword,
    avatars,
    error,
    setError,
    onSuccess,
    initialView,
}) => {
    const [isLoginView, setIsLoginView] = useState(initialView === 'login');
    const [isForgotPasswordView, setIsForgotPasswordView] = useState(false);
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState(avatars[0]);
    const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);
    const [emailOptIn, setEmailOptIn] = useState(true);

    useEffect(() => {
        setIsLoginView(initialView === 'login');
        setIsForgotPasswordView(false);
    }, [initialView]);

    const handleToggleView = () => {
        setIsLoginView(!isLoginView);
        setIsForgotPasswordView(false);
        setError('');
        setEmail('');
        setConfirmEmail('');
        setPassword('');
        setDisplayName('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (isForgotPasswordView) {
            try {
                await onForgotPassword(email);
                setIsForgotPasswordView(false); // Go back to login on success
            } catch (err) {
                let friendlyMessage = "Failed to send reset email. Please try again.";
                if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
                    friendlyMessage = "No account found with that email address.";
                }
                setError(friendlyMessage);
            }
            return;
        }

        let success = false;
        if (isLoginView) {
            success = await onLogin(email, password);
        } else {
            if (email !== confirmEmail) {
                setError("Emails do not match. Please check and try again.");
                return;
            }
            const avatarUrl = `https://placehold.co/100x100/${selectedAvatar.color}/ffffff?text=${encodeURIComponent(selectedAvatar.emoji)}`;
            success = await onRegister(email, password, displayName, avatarUrl, leaderboardOptIn, emailOptIn);
        }
        if (success) {
            onSuccess();
        }
    };

    const handleForgotPasswordClick = () => {
        setError('');
        setIsForgotPasswordView(true);
    };

    if (isForgotPasswordView) {
        return (
            <div className="bg-[#211a6e] rounded-xl shadow-2xl p-8 border border-gray-700">
                <CreateHerFestLogo className="h-20 mx-auto mb-4" />
                <h2 className="text-center text-3xl font-bold text-white mb-2">Reset Password</h2>
                <p className="text-center text-gray-300 mb-6">Enter your email to receive a reset link.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        type="email"
                        placeholder="Email address"
                        required
                        className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': brandColors.palatinateBlue }}
                    />
                    {error && <p className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded-md">{error}</p>}
                    <button
                        type="submit"
                        style={{ backgroundColor: brandColors.palatinateBlue }}
                        className="w-full flex justify-center py-3 px-4 border border-transparent text-lg font-bold rounded-md text-white hover:opacity-80"
                    >
                        Send Reset Link
                    </button>
                </form>
                <p className="text-center text-sm text-gray-400 mt-6">
                    Remembered your password?
                    <button
                        onClick={() => setIsForgotPasswordView(false)}
                        style={{ color: brandColors.wisteria }}
                        className="font-medium hover:opacity-80 ml-2"
                    >
                        Back to Login
                    </button>
                </p>
            </div>
        );
    }

    return (
        <div className="bg-[#211a6e] rounded-xl shadow-2xl p-8 border border-gray-700">
            <CreateHerFestLogo className="h-20 mx-auto mb-4" />
            <h2 className="text-center text-3xl font-bold text-white mb-2">{isLoginView ? 'Log In' : 'Join the Challenge!'}</h2>
            <p className="text-center text-gray-300 mb-6">{isLoginView ? 'Welcome back!' : 'Create an account to start playing.'}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    type="email"
                    placeholder="Email address"
                    required
                    className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': brandColors.palatinateBlue }}
                />
                {!isLoginView && (
                    <>
                        <input
                            value={confirmEmail}
                            onChange={e => setConfirmEmail(e.target.value)}
                            type="email"
                            placeholder="Confirm email address"
                            required
                            className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': brandColors.palatinateBlue }}
                        />
                        <input
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                            type="text"
                            placeholder="Display Name / Nickname"
                            required
                            className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': brandColors.palatinateBlue }}
                        />
                    </>
                )}
                <input
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    type="password"
                    placeholder="Password"
                    required
                    className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': brandColors.palatinateBlue }}
                />
                {isLoginView && (
                     <div className="text-right">
                        <button
                            type="button"
                            onClick={handleForgotPasswordClick}
                            style={{ color: brandColors.wisteria }}
                            className="text-sm font-medium hover:opacity-80"
                        >
                            Forgot Password?
                        </button>
                    </div>
                )}
                {!isLoginView && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Choose your avatar:</label>
                            <div className="flex justify-center space-x-2">
                                {avatars.map(avatar => (
                                    <span
                                        key={avatar.emoji}
                                        role="button"
                                        tabIndex="0"
                                        aria-label={`avatar ${avatar.emoji}`}
                                        onClick={() => setSelectedAvatar(avatar)}
                                        onKeyPress={e => e.key === 'Enter' && setSelectedAvatar(avatar)}
                                        className={`h-12 w-12 rounded-full cursor-pointer transition-transform transform hover:scale-110 flex items-center justify-center text-3xl ${selectedAvatar.emoji === avatar.emoji ? 'ring-2 ring-offset-2 ring-offset-[#211a6e]' : ''}`}
                                        style={{ backgroundColor: `#${avatar.color}`, '--tw-ring-color': brandColors.palatinateBlue }}
                                    >
                                        {avatar.emoji}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    id="leaderboardOptIn"
                                    type="checkbox"
                                    checked={leaderboardOptIn}
                                    onChange={e => setLeaderboardOptIn(e.target.checked)}
                                    className="h-4 w-4 rounded bg-gray-700 border-gray-500 focus:ring-offset-0"
                                    style={{ color: brandColors.palatinateBlue, '--tw-ring-color': brandColors.palatinateBlue }}
                                />
                                <label htmlFor="leaderboardOptIn" className="ml-2 block text-sm text-gray-400">Show my display name on the leaderboard</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    id="emailOptIn"
                                    type="checkbox"
                                    checked={emailOptIn}
                                    onChange={e => setEmailOptIn(e.target.checked)}
                                    className="h-4 w-4 rounded bg-gray-700 border-gray-500 focus:ring-offset-0"
                                    style={{ color: brandColors.palatinateBlue, '--tw-ring-color': brandColors.palatinateBlue }}
                                />
                                <label htmlFor="emailOptIn" className="ml-2 block text-sm text-gray-400">Receive email updates about new challenges</label>
                            </div>
                        </div>
                    </>
                )}
                {error && <p className="text-red-400 text-sm text-center bg-red-500/10 p-2 rounded-md">{error}</p>}
                <button
                    type="submit"
                    style={{
                        backgroundColor: brandColors.palatinateBlue,
                        '--tw-ring-color': brandColors.palatinateBlue,
                        '--tw-ring-offset-color': '#211a6e',
                    }}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-lg font-bold rounded-md text-white hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                    {isLoginView ? 'Log In' : 'Join Now'}
                </button>
            </form>
            <p className="text-center text-sm text-gray-400 mt-6">
                {isLoginView ? "Don't have an account?" : 'Already have an account?'}
                <button
                    onClick={handleToggleView}
                    style={{ color: brandColors.wisteria }}
                    className="font-medium hover:opacity-80 ml-2"
                >
                    {isLoginView ? 'Register here' : 'Log in here'}
                </button>
            </p>
        </div>
    );
};

const ChallengePage = ({ user, challenge, onSubmit, onBack, onImageZoom, callGetHint, userData, db, appId, submissions }) => {
    
    const getInitialAnswerState = () => {
        switch (challenge.answerType) {
            case 'multi_part_text': return {};
            case 'sequence': return [];
            case 'multi_select': return [];
            case 'advanced_multi_part': return { part1: '', part2: [], part3: [] };
            default: return '';
        }
    };
    
    const [answer, setAnswer] = useState(getInitialAnswerState);
    const [hint, setHint] = useState('');
    const [isHintLoading, setIsHintLoading] = useState(false);
    const [advancedHelp, setAdvancedHelp] = useState('');
    const [isAdvancedHelpLoading, setIsAdvancedHelpLoading] = useState(false);
    const [riskExplanation, setRiskExplanation] = useState('');
    const [isRiskLoading, setIsRiskLoading] = useState(false);
    const [activeArtifactIndex, setActiveArtifactIndex] = useState(0);
    const [attemptCount, setAttemptCount] = useState(0);
    const [showHintGlow, setShowHintGlow] = useState(false);
    const [lastSubmission, setLastSubmission] = useState(null);

     useEffect(() => {
        if (user && !user.isAnonymous && challenge && db) {
            const attemptsQuery = query(
                collection(db, `artifacts/${appId}/users/${user.uid}/challengeAttempts`),
                where("challengeId", "==", challenge.id)
            );
            
            const unsub = onSnapshot(attemptsQuery, (snapshot) => {
                setAttemptCount(snapshot.size);
                if (snapshot.size >= 2) {
                    setShowHintGlow(true);
                }
            });
            return () => unsub();
        }
    }, [user, challenge, db, appId]);


    useEffect(() => { 
        setHint('');
        setAdvancedHelp('');
        setRiskExplanation('');
        setActiveArtifactIndex(0);
        setAnswer(getInitialAnswerState());
        setLastSubmission(submissions[challenge.id] || null);
    }, [submissions, challenge]);
    
    const isChallengeUnlocked = (challenge.unlockDate ? new Date() >= new Date(challenge.unlockDate) : true) || userData?.isAdmin;

    if (!isChallengeUnlocked) {
        return (
            <div className="text-center max-w-2xl mx-auto bg-[#211a6e] p-8 rounded-lg border border-gray-700">
                <LockIcon />
                <h1 className="text-3xl font-bold mt-4">Challenge Locked</h1>
                <p className="text-gray-300 mt-2">
                    This challenge is not yet available. It will unlock on ${new Date(challenge.unlockDate).toLocaleDateString()}.
                </p>
                <button onClick={onBack} style={{backgroundColor: brandColors.palatinateBlue}} className="mt-6 hover:opacity-80 text-white font-bold py-2 px-6 rounded-lg transition-colors">
                    &larr; Back to Home
                </button>
            </div>
        );
    }
    
    const partFeedback = lastSubmission?.partFeedback;
    const isStuck = attemptCount >= 4 && lastSubmission && lastSubmission.score > 0 && !lastSubmission.isCorrect;

    const handleGetHint = async () => {
        setIsHintLoading(true);
        const systemPrompt = "You are a helpful cybersecurity tutor for the CreateHer Fest. You provide encouraging and subtle hints for cybersecurity challenges. Do not give away the answer directly. Guide the user toward the correct way of thinking. Keep your hints to 1-2 sentences.";
        const userQuery = `The user is working on a challenge titled '${challenge.title}'. The scenario is: '${challenge.scenario}'. The task is: '${challenge.instructions}'. Provide a gentle hint to help them solve it.`;
        
        const generatedHint = await callGetHint(systemPrompt, userQuery);
        setHint(generatedHint);
        setIsHintLoading(false);
    };

    const handleGetAdvancedHelp = async () => {
        setIsAdvancedHelpLoading(true);
         let incorrectParts = "the user is stuck in general.";
        if (partFeedback) {
            const incorrectKeys = Object.keys(partFeedback).filter(key => !partFeedback[key]);
            if (incorrectKeys.length > 0) {
                 incorrectParts = `the user got the following parts wrong: ${incorrectKeys.join(', ')}.`;
            }
        }

        const systemPrompt = "You are an expert cybersecurity mentor for the CreateHer Fest. A user is stuck on a challenge after multiple attempts. They have some parts right but are missing others. Your task is to provide a detailed, contextual explanation that helps them understand the missing piece *without* giving away the direct answer. Focus on the 'why' behind the security concept they are missing for the specific parts they got wrong. Explain the concept and guide them on how to apply it to the problem. Your tone should be encouraging and educational.";
        const userQuery = `A user is stuck on the challenge: '${challenge.title}'. The scenario is: '${challenge.scenario}'. Their last attempt had feedback, and ${incorrectParts} Please provide a clear explanation for the incorrect parts only to help them get unstuck.`;
        const helpText = await callGetHint(systemPrompt, userQuery);
        setAdvancedHelp(helpText);
        setIsAdvancedHelpLoading(false);
    };

    const handleExplainRisk = async () => {
        setIsRiskLoading(true);
        const systemPrompt = "You are a senior cybersecurity analyst explaining the importance of a security concept to a junior team member for the CreateHer Fest. Your tone is encouraging, clear, and focused on real-world impact. Use analogies if helpful. Keep it concise (2-3 sentences).";
        const userQuery = `A user just solved a challenge about '${challenge.title}'. The scenario was: '${challenge.scenario}'. Explain in a concise paragraph why this is a significant security risk in the real world, what kind of data could be exposed, and what the consequences for a company might be.`;

        const explanation = await callGetHint(systemPrompt, userQuery);
        setRiskExplanation(explanation);
        setIsRiskLoading(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = await onSubmit(challenge, answer, lastSubmission?.score || 0);
        if (user.isAnonymous && result) {
            setLastSubmission(result);
            setAttemptCount(prev => prev + 1);
        }
        setAnswer(getInitialAnswerState());
    };

    const handleMultiSelectChange = (option) => { const newAnswer = (answer.part3 || []).includes(option) ? (answer.part3 || []).filter(item => item !== option) : [...(answer.part3 || []), option]; setAnswer(prev => ({...prev, part3: newAnswer})); };
    const handleSequenceChange = (fromIndex, toIndex) => { const currentAnswer = answer.part2 && answer.part2.length > 0 ? answer.part2 : (challenge.validation?.options?.part2 || []); const newAnswer = [...currentAnswer]; const [movedItem] = newAnswer.splice(fromIndex, 1); newAnswer.splice(toIndex, 0, movedItem); setAnswer(prev => ({...prev, part2: newAnswer})); };
    
    const activeArtifact = challenge.artifacts && challenge.artifacts[activeArtifactIndex];
    const nextArtifact = () => setActiveArtifactIndex(prev => Math.min(prev + 1, challenge.artifacts.length - 1));
    const prevArtifact = () => setActiveArtifactIndex(prev => Math.max(prev - 1, 0));

    const renderAnswerField = () => {
        const v = challenge.validation;
        if (!v) return <p className="text-red-400">Challenge validation data is missing or loading...</p>;

        switch (challenge.answerType) {
            case 'multi_part_text': return (<div className="space-y-4">{Object.keys(v.correctAnswers || {}).map((key) => (<div key={key}><label className="flex items-center text-sm font-medium text-gray-400 mb-1">{v.labels?.[key] || `Part ${key}`}{partFeedback && (partFeedback[key] ? <FeedbackCheckIcon /> : <FeedbackXIcon />)}</label><input type="text" value={answer[key] || ''} onChange={(e) => setAnswer({...answer, [key]: e.target.value})} className="w-full p-2 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2" style={{'--tw-ring-color': brandColors.palatinateBlue}} /></div>))}</div>);
            case 'sequence': const currentSequence = answer && answer.length > 0 ? answer : (v.options || []); return (<div><p className="text-gray-400 mb-2">Click the arrows to reorder the items.</p><div className="space-y-2">{currentSequence.map((option, index) => (<div key={index} className="bg-gray-700 p-3 rounded-lg flex items-center justify-between"><span>{option}</span><div><button type="button" disabled={index === 0} onClick={() => { const newSeq = [...currentSequence]; const [moved] = newSeq.splice(index, 1); newSeq.splice(index - 1, 0, moved); setAnswer(newSeq); }} className="px-2 disabled:opacity-50">↑</button><button type="button" disabled={index === (v.options.length - 1)} onClick={() => { const newSeq = [...currentSequence]; const [moved] = newSeq.splice(index, 1); newSeq.splice(index + 1, 0, moved); setAnswer(newSeq); }} className="px-2 disabled:opacity-50">↓</button></div></div>))}</div></div>);
            case 'multi_select': return (<div className="space-y-2">{(v.options || []).map((option, index) => (<label key={index} className="flex items-center bg-gray-700 p-3 rounded-lg cursor-pointer hover:bg-gray-600"><input type="checkbox" value={option} checked={(answer || []).includes(option)} onChange={() => setAnswer(prev => prev.includes(option) ? prev.filter(i => i !== option) : [...prev, option] )} className="form-checkbox h-5 w-5 bg-gray-900 border-gray-600 focus:ring-offset-0" style={{color: brandColors.palatinateBlue, '--tw-ring-color': brandColors.palatinateBlue}} /><span className="ml-3 text-white">{option}</span></label>))}</div>);
            case 'advanced_multi_part': const currentSeqPart2 = answer.part2 && answer.part2.length > 0 ? answer.part2 : (v.options?.part2 || []); return (<div className="space-y-6"><div><label className="flex items-center text-lg font-medium mb-2">{v.labels?.part1 || 'Part 1'} {partFeedback && (partFeedback.part1 ? <FeedbackCheckIcon /> : <FeedbackXIcon />)} </label><input type="text" value={answer.part1 || ''} onChange={(e) => setAnswer({...answer, part1: e.target.value})} className="w-full p-2 bg-gray-900 border border-gray-700 rounded-md" /></div><div><label className="flex items-center text-lg font-medium mb-2">{v.labels?.part2 || 'Part 2 (Sequence)'} {partFeedback && (partFeedback.part2 ? <FeedbackCheckIcon /> : <FeedbackXIcon />)}</label>{v.options?.part2 && v.options.part2.length > 0 ? (<div className="space-y-2">{currentSeqPart2.map((option, index) => (<div key={index} className="bg-gray-700 p-3 rounded-lg flex items-center justify-between"><span>{option}</span><div><button type="button" disabled={index === 0} onClick={() => handleSequenceChange(index, index - 1)} className="px-2 disabled:opacity-50">↑</button><button type="button" disabled={index === (v.options.part2.length - 1)} onClick={() => handleSequenceChange(index, index + 1)} className="px-2 disabled:opacity-50">↓</button></div></div>))}</div>) : (<p className="text-sm text-yellow-400 bg-yellow-500/10 p-2 rounded-md">This part is misconfigured.</p>)}</div><div><label className="flex items-center text-lg font-medium mb-2">{v.labels?.part3 || 'Part 3 (Multi-Select)'} {partFeedback && (partFeedback.part3 ? <FeedbackCheckIcon /> : <FeedbackXIcon />)}</label>{v.options?.part3 && v.options.part3.length > 0 ? ((v.options.part3 || []).map((option, index) => (<label key={index} className="flex items-center bg-gray-700 p-3 rounded-lg cursor-pointer hover:bg-gray-600 mt-2"><input type="checkbox" value={option.id} checked={(answer.part3 || []).includes(option.id)} onChange={() => handleMultiSelectChange(option.id)} className="form-checkbox h-5 w-5" style={{color: brandColors.palatinateBlue}} /><span className="ml-3 text-white">{option.text}</span></label>))) : (<p className="text-sm text-yellow-400 bg-yellow-500/10 p-2 rounded-md">This part is misconfigured.</p>)}</div></div>);
            default: return <input type="text" value={answer || ''} onChange={(e) => setAnswer(e.target.value)} className="w-full p-2 bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2" style={{'--tw-ring-color': brandColors.palatinateBlue}} placeholder="Type your answer here" />;
        }
    };

    const hintButtonGlowingStyle = showHintGlow ? {
        boxShadow: `0 0 15px ${brandColors.wisteria}`,
        animation: 'pulse 2s infinite'
    } : {};
    
    return (
        <div className="max-w-4xl mx-auto">
            <button onClick={onBack} style={{color: brandColors.wisteria}} className="mb-6 hover:opacity-80">&larr; Back to Home</button>
            <div className="bg-[#211a6e] rounded-lg shadow-xl p-6 md:p-8 border border-gray-700">
                <div className="flex flex-col md:flex-row justify-between md:items-start mb-6">
                    <div className="mb-4 md:mb-0">
                        <h1 className="text-3xl md:text-4xl font-bold">{challenge.title}</h1>
                        <span className="text-xl md:text-2xl font-bold" style={{color: brandColors.wisteria}}>{challenge.points} Points</span>
                    </div>
                    <button onClick={handleGetHint} disabled={isHintLoading || attemptCount < 2} className="relative flex-shrink-0 w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={hintButtonGlowingStyle}>
                        <SparkleIcon className="h-5 w-5" />
                        <span>{isHintLoading ? 'Thinking...' : 'Get a Hint'}</span>
                        {attemptCount < 2 && <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap">Unlocks after 2 attempts</span>}
                        {showHintGlow && <span className="absolute top-0 right-0 block h-3 w-3 rounded-full ring-2 ring-gray-800 bg-yellow-400 animate-ping"></span>}
                    </button>
                </div>

                 {hint && (<div className="bg-blue-500/10 border border-blue-400/30 text-blue-200 p-4 rounded-lg mb-6"><h4 className="font-bold">✨ Hint</h4><div dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(hint) }} /></div>)}
                {isStuck && (
                    <div className="my-6 p-4 bg-purple-500/10 border border-purple-400/30 rounded-lg text-center">
                        <h4 className="font-bold text-purple-200">Feeling Stuck?</h4>
                        <p className="text-sm text-purple-300 mb-3">You've made some great attempts! Click here for some extra help to get you over the finish line.</p>
                        <button onClick={handleGetAdvancedHelp} disabled={isAdvancedHelpLoading} className="flex items-center justify-center mx-auto gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">
                            <HelpIcon/>
                            {isAdvancedHelpLoading ? 'Thinking...' : 'Get Advanced Help'}
                        </button>
                    </div>
                )}
                {advancedHelp && (<div className="bg-purple-500/10 border border-purple-400/30 text-purple-200 p-4 rounded-lg mb-6"><h4 className="font-bold">✨ Let's Break It Down...</h4><div dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(advancedHelp) }} /></div>)}
                
                <div className="my-6">{challenge.artifacts && challenge.artifacts.length > 0 && (<div><div className="flex items-center justify-between mb-2"><button onClick={prevArtifact} disabled={activeArtifactIndex === 0} className="p-2 rounded-full bg-black/20 hover:bg-black/40 disabled:opacity-50"><ChevronLeft/></button><div className="text-center"><h4 className="font-bold text-lg">{activeArtifact.title}</h4><p className="text-sm text-gray-400">{activeArtifactIndex + 1} / {challenge.artifacts.length}</p></div><button onClick={nextArtifact} disabled={activeArtifactIndex === challenge.artifacts.length - 1} className="p-2 rounded-full bg-black/20 hover:bg-black/40 disabled:opacity-50"><ChevronRight/></button></div><div className="bg-[#150e60] p-4 rounded-lg">{activeArtifact.type === 'image' ? (<img src={activeArtifact.content} alt={activeArtifact.title || "Challenge artifact"} className="rounded-lg w-full max-w-lg mx-auto bg-gray-800 cursor-zoom-in" onClick={() => onImageZoom(activeArtifact.content)} onError={(e) => { e.target.onerror = null; e.target.src=`https://placehold.co/600x400/150e60/f1eae7?text=Image+Load+Failed`; e.target.alt="Image URL failed to load."; }} />) : (<pre className="bg-black text-white p-4 rounded-md overflow-x-auto text-sm whitespace-pre-wrap"><code>{activeArtifact.content}</code></pre>)}</div></div>)}</div>

                <div className="prose prose-invert max-w-none prose-headings:text-white mb-6">
                    <h3 className="text-xl font-semibold border-b border-gray-600 pb-2 mb-4" style={{color: brandColors.wisteria}}>Scenario</h3>
                    <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(challenge.scenario) }} />
                    <h3 className="text-xl font-semibold border-b border-gray-600 pb-2 my-4" style={{color: brandColors.wisteria}}>Your Task</h3>
                    <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(challenge.instructions) }} />
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4"><label className="block text-lg font-medium mb-2">Your Answer</label>{renderAnswerField()}</div>
                    {lastSubmission && (<div className={`p-4 rounded-md text-sm mb-4 ${lastSubmission.isCorrect ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}><p>Your last submission earned {lastSubmission.score} points. You can resubmit if you'd like. ({attemptCount} attempt{attemptCount !== 1 ? 's' : ''})</p>{lastSubmission.isCorrect && (<div className="mt-4 space-y-4"><div className="flex items-center space-x-4"><button type="button" onClick={handleExplainRisk} disabled={isRiskLoading} className="flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"><SparkleIcon className="h-5 w-5 mr-2" />{isRiskLoading ? 'Explaining...' : 'Explain the Real-World Risk'}</button></div>{riskExplanation && (<div className="mt-4 bg-purple-500/10 border border-purple-400/30 text-purple-200 p-4 rounded-lg"><h4 className="font-bold">✨ The Real-World Impact</h4><div dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(riskExplanation) }} /></div>)}</div>)}</div>)}
                    <div className="flex justify-end"><button type="submit" style={{backgroundColor: brandColors.palatinateBlue}} className="hover:opacity-80 text-white font-bold py-2 px-6 rounded-lg transition-colors">Submit</button></div>
                </form>
            </div>
        </div>
    );
};
  
const ChallengeEditor = ({ challenge, db, appId, showNotification }) => {
    const [localChallenge, setLocalChallenge] = useState({
        ...challenge,
        artifacts: challenge.artifacts || [], 
        validation: JSON.stringify(challenge.validation, null, 2)
    });
    const [isSaving, setIsSaving] = useState(false);
    const [jsonError, setJsonError] = useState('');

    useEffect(() => {
        setLocalChallenge({
            ...challenge,
            artifacts: challenge.artifacts || [],
            validation: JSON.stringify(challenge.validation, null, 2)
        });
    }, [challenge]);

    const handleUpdate = async () => {
        setIsSaving(true);
        setJsonError('');
        try {
            const { id, ...dataToUpdate } = localChallenge;
            dataToUpdate.validation = JSON.parse(dataToUpdate.validation);
            dataToUpdate.points = parseInt(dataToUpdate.points, 10);
            dataToUpdate.order = parseInt(dataToUpdate.order, 10);
            
            const docRef = doc(db, `artifacts/${appId}/public/data/challenges`, id);
            await updateDoc(docRef, dataToUpdate);
            showNotification('Challenge updated successfully!', 'success');
        } catch (error) {
            console.error("Error updating challenge:", error);
            if (error instanceof SyntaxError) {
                setJsonError("Invalid JSON in Validation field. Please check your syntax.");
                showNotification("Invalid JSON in Validation. Please correct and try again.", 'error');
            } else {
                 showNotification(`Error: ${error.message}`, 'error');
            }
        }
        setIsSaving(false);
    };

    const handleDelete = async () => {
        const adminConfirm = prompt("Type the challenge title to confirm deletion:");
        if (adminConfirm !== localChallenge.title) {
            showNotification("Deletion cancelled. Title did not match.", "info");
            return;
        }

        setIsSaving(true);
        try {
            const docRef = doc(db, `artifacts/${appId}/public/data/challenges`, localChallenge.id);
            await deleteDoc(docRef);
            showNotification('Challenge deleted successfully!', 'success');
        } catch (error) {
            console.error("Error deleting challenge:", error);
            showNotification(`Error: ${error.message}`, 'error');
            setIsSaving(false);
        }
    };
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocalChallenge(prev => ({ ...prev, [name]: value }));
    };

    const handleArtifactChange = (index, field, value) => {
        const newArtifacts = localChallenge.artifacts.map((artifact, i) => {
            if (i === index) {
                return { ...artifact, [field]: value };
            }
            return artifact;
        });
        setLocalChallenge(prev => ({ ...prev, artifacts: newArtifacts }));
    };

    const addArtifact = () => {
        setLocalChallenge(prev => ({
            ...prev,
            artifacts: [...(prev.artifacts || []), { title: '', type: 'text', content: '' }]
        }));
    };

    const removeArtifact = (index) => {
        const newArtifacts = localChallenge.artifacts.filter((_, i) => i !== index);
        setLocalChallenge(prev => ({ ...prev, artifacts: newArtifacts }));
    };


    return (
        <div className="bg-[#150e60] p-4 rounded-lg border border-gray-700 mb-6">
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Editing: {localChallenge.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="title" value={localChallenge.title} onChange={handleChange} placeholder="Title" className="bg-gray-900 p-2 rounded-md border border-gray-600" />
                <input name="unlockDate" value={localChallenge.unlockDate} onChange={handleChange} placeholder="Unlock Date (YYYY-MM-DDTHH:MM:SS)" className="bg-gray-900 p-2 rounded-md border border-gray-600" />
                <input name="points" type="number" value={localChallenge.points} onChange={handleChange} placeholder="Points" className="bg-gray-900 p-2 rounded-md border border-gray-600" />
                <input name="difficulty" value={localChallenge.difficulty} onChange={handleChange} placeholder="Difficulty (Beginner, etc.)" className="bg-gray-900 p-2 rounded-md border border-gray-600" />
                <input name="order" type="number" value={localChallenge.order} onChange={handleChange} placeholder="Order" className="bg-gray-900 p-2 rounded-md border border-gray-600" />
                <input name="badgeImageUrl" value={localChallenge.badgeImageUrl || ''} onChange={handleChange} placeholder="Badge Image URL" className="md:col-span-2 bg-gray-900 p-2 rounded-md border border-gray-600" />
                
                <textarea name="scenario" value={localChallenge.scenario} onChange={handleChange} placeholder="Scenario" rows="3" className="md:col-span-2 bg-gray-900 p-2 rounded-md border border-gray-600" />
                <textarea name="instructions" value={localChallenge.instructions} onChange={handleChange} placeholder="Instructions" rows="3" className="md:col-span-2 bg-gray-900 p-2 rounded-md border border-gray-600" />
                
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Artifacts</label>
                    <div className="space-y-4">
                        {(localChallenge.artifacts || []).map((artifact, index) => (
                            <div key={index} className="bg-gray-800 p-3 rounded-lg border border-gray-600">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                                    <input
                                        value={artifact.title}
                                        onChange={(e) => handleArtifactChange(index, 'title', e.target.value)}
                                        placeholder="Artifact Title"
                                        className="bg-gray-900 p-2 rounded-md border border-gray-700"
                                    />
                                    <select
                                        value={artifact.type}
                                        onChange={(e) => handleArtifactChange(index, 'type', e.target.value)}
                                        className="bg-gray-900 p-2 rounded-md border border-gray-700 h-full"
                                    >
                                        <option value="text">Text / Code</option>
                                        <option value="image">Image URL</option>
                                    </select>
                                     <button
                                        onClick={() => removeArtifact(index)}
                                        className="bg-red-800 hover:bg-red-700 text-white font-bold py-2 px-3 rounded text-sm"
                                    >
                                        Remove Artifact
                                    </button>
                                </div>
                                <textarea
                                    value={artifact.content}
                                    onChange={(e) => handleArtifactChange(index, 'content', e.target.value)}
                                    placeholder={artifact.type === 'image' ? 'https://example.com/image.png' : 'Enter code or text here...'}
                                    rows="4"
                                    className="font-mono text-sm w-full bg-gray-900 p-2 rounded-md border border-gray-700 mt-2"
                                />
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={addArtifact}
                        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
                    >
                        + Add Artifact
                    </button>
                </div>
                
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Validation (JSON)</label>
                    <textarea name="validation" value={localChallenge.validation} onChange={handleChange} placeholder="Validation JSON" rows="8" className="font-mono text-sm w-full bg-gray-900 p-2 rounded-md border border-gray-600" />
                </div>
            </div>
            {jsonError && <p className="text-red-400 text-sm mt-2">{jsonError}</p>}
            <div className="flex justify-end space-x-4 mt-4">
                <button onClick={handleDelete} disabled={isSaving} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">Delete</button>
                <button onClick={handleUpdate} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">{isSaving ? 'Saving...' : 'Update'}</button>
            </div>
        </div>
    );
};

const GeneratedBadge = ({ challenge, hasBadge, onShowModal, viewedBadges }) => {
    const isUnviewed = hasBadge && !viewedBadges?.includes(challenge.id);
    const glowingStyle = {
        boxShadow: `0 0 15px ${brandColors.wisteria}80`,
    };

    if (!hasBadge) {
        return (
            <div className="relative p-4 bg-[#211a6e] rounded-lg text-center opacity-60">
                <BadgeIcon size={64} className="mx-auto text-gray-600" />
                <p className="mt-2 text-sm font-bold text-gray-500">{challenge.title}</p>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <LockIcon />
                </div>
            </div>
        );
    }
    
    return (
        <div 
            onClick={() => hasBadge && challenge.badgeImageUrl && onShowModal(challenge.badgeImageUrl, challenge.title, challenge.id)}
            className={`relative p-4 bg-[#211a6e] rounded-lg text-center border-2 shadow-lg transition-transform transform hover:scale-105 ${hasBadge && challenge.badgeImageUrl ? 'cursor-pointer' : ''}`} 
            style={{borderColor: brandColors.wisteria, ...(isUnviewed ? glowingStyle : {})}}
        >
            {challenge.badgeImageUrl ? 
                <img src={challenge.badgeImageUrl} alt={`Badge for ${challenge.title}`} className="h-16 w-16 rounded-full mx-auto" />
                : 
                <BadgeIcon size={64} className="mx-auto" style={{color: brandColors.wisteria}} />
            }
            <p className="mt-2 text-sm font-bold text-white">{challenge.title}</p>
             <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                UNLOCKED
            </div>
        </div>
    );
};

const RewardsPage = ({ userData, challenges, onShowBadge }) => {
    const earnedBadges = userData?.badges || [];
    const allChallengesCompleted = challenges.length > 0 && earnedBadges.length === challenges.length;

    const earnedChallenges = challenges.filter(challenge => earnedBadges.includes(challenge.id));
    const lockedChallenges = challenges.filter(challenge => !earnedBadges.includes(challenge.id));

    return (
        <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
                <h1 className="text-4xl font-bold" style={{color: brandColors.wisteria}}>My Badges</h1>
                <p className="text-gray-300 mt-2">Here are the unique badges you've earned for completing challenges!</p>
            </div>
            {allChallengesCompleted && (
                <div className="bg-yellow-500/10 border-2 border-dashed border-yellow-400/30 text-yellow-200 p-6 rounded-lg mb-8 text-center">
                    <TrophyIcon size={32} className="mx-auto mb-2" />
                    <h2 className="text-2xl font-bold mt-4">Cyber Champion!</h2>
                    <p>Congratulations! You've completed all the challenges.</p>
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {earnedChallenges.map(challenge => (
                    <GeneratedBadge key={challenge.id} challenge={challenge} hasBadge={true} onShowModal={onShowBadge} viewedBadges={userData?.viewedBadges} />
                ))}
                {lockedChallenges.map(challenge => (
                    <GeneratedBadge key={challenge.id} challenge={challenge} hasBadge={false} />
                ))}
            </div>
        </div>
    );
};

const PrivacyPolicyPage = () => (
    <div className="max-w-4xl mx-auto prose prose-invert prose-p:text-gray-300 prose-headings:text-white">
        <h1>Privacy Policy</h1>
        <p>Your privacy is important to us. This policy explains what data we collect and why.</p>
        <h2>Data We Collect</h2>
        <p>We only collect the minimum information necessary for you to participate in the CybHER Challenge:</p>
        <ul>
            <li><strong>Email Address:</strong> Used for registration, login, and important notifications about the challenge.</li>
            <li><strong>Display Name/Nickname:</strong> A name you choose that will be displayed on the public leaderboard if you opt-in.</li>
            <li><strong>Avatar Selection:</strong> The avatar you choose to represent you.</li>
        </ul>
        <h2>How We Use Your Data</h2>
        <p>Your data is used solely for the purposes of running this event, including scoring, leaderboard ranking, prize notifications, and event-related announcements. We will not sell or share your personal data with third parties for marketing purposes.</p>
        <h2>Data Retention</h2>
        <p>All user data will be deleted within 90 days following the conclusion of the event.</p>
    </div>
);

const ProfilePage = ({ userData, avatars, db, user, appId, showNotification, onNavigate }) => {
    const [displayName, setDisplayName] = useState(userData?.displayName || '');
    const [emailOptIn, setEmailOptIn] = useState(userData?.emailOptIn || false);
    const [isSaving, setIsSaving] = useState(false);
    const [isJoining, setIsJoining] = useState(false);

    const findLocalAvatarFromUrl = (url) => {
        if (!url) return avatars[0];
        const match = url.match(/text=(.*?)$/);
        if (!match) return avatars[0];
        const emoji = decodeURIComponent(match[1]);
        return avatars.find(a => a.emoji === emoji) || avatars[0];
    };

    const [selectedAvatar, setSelectedAvatar] = useState(findLocalAvatarFromUrl(userData?.avatar));

    const handleSave = async (e) => {
        e.preventDefault();
        if (!displayName.trim()) {
            showNotification('Display name cannot be empty.', 'error');
            return;
        }
        setIsSaving(true);
        try {
            const userRef = doc(db, `artifacts/${appId}/public/data/users`, user.uid);
            const avatarUrl = `https://placehold.co/100x100/${selectedAvatar.color}/ffffff?text=${encodeURIComponent(selectedAvatar.emoji)}`;
            await updateDoc(userRef, {
                displayName: displayName.trim(),
                avatar: avatarUrl,
                emailOptIn: emailOptIn
            });
            showNotification('Profile updated successfully!', 'success');
        } catch (error) {
            console.error("Error updating profile:", error);
            showNotification('Failed to update profile. Please try again.', 'error');
        }
        setIsSaving(false);
    };

    const handleJoinLeaderboard = async () => {
        if (userData?.leaderboardOptIn) {
            showNotification('You are already on the leaderboard.', 'info');
            return;
        }
        setIsJoining(true);
        try {
            const userRef = doc(db, `artifacts/${appId}/public/data/users`, user.uid);
            await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);

                if (!userDoc.exists()) {
                    throw new Error("User profile not found for leaderboard join.");
                }

                const data = userDoc.data();
                const currentPoints = data.totalPoints || 0;

                // Use the current breakdown, defaulting to zero if the field is missing
                const currentBreakdown = data.pointsBreakdown || { base: 0, challenges: 0, bonus: 0 };
                const basePointsToAdd = 10 - currentBreakdown.base;

                // Only update score if they haven't already received base points
                if (basePointsToAdd > 0) {
                    transaction.update(userRef, {
                        leaderboardOptIn: true,
                        totalPoints: currentPoints + basePointsToAdd,
                        // --- CRITICAL FIX: Initialize all breakdown fields ---
                        "pointsBreakdown.base": 10,
                        "pointsBreakdown.challenges": currentBreakdown.challenges,
                        "pointsBreakdown.bonus": currentBreakdown.bonus,
                    });
                } else {
                    // If base points were already 10, just set the opt-in flag.
                    transaction.update(userRef, { leaderboardOptIn: true });
                }
            });
            showNotification('You have joined the leaderboard and earned 10 bonus points!', 'success');
        } catch (error) {
            console.error("Error joining leaderboard:", error);
            showNotification('Failed to join the leaderboard. Please try again.', 'error');
        }
        setIsJoining(false);
    };

    if (!userData) {
        return (
            <div className="text-center">
                <p>Loading profile...</p>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-[#211a6e] rounded-lg shadow-xl p-8 border border-gray-700">
                <h1 className="text-3xl font-bold text-center mb-6" style={{color: brandColors.wisteria}}>Edit Your Profile</h1>
                <form onSubmit={handleSave} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            value={userData?.email || ''}
                            disabled
                            className="w-full px-4 py-3 bg-[#150e60]/50 border border-gray-600 rounded-md text-gray-400 cursor-not-allowed"
                        />
                    </div>
                    <div>
                        <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                        <input 
                            id="displayName"
                            type="text" 
                            value={displayName} 
                            onChange={(e) => setDisplayName(e.target.value)} 
                            required 
                            className="w-full px-4 py-3 bg-[#150e60] border border-gray-600 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Choose your avatar:</label>
                        <div className="flex flex-wrap justify-center gap-2">
                            {avatars.map(avatar => (
                                <span
                                    key={avatar.emoji}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={`avatar ${avatar.emoji}`}
                                    onClick={() => setSelectedAvatar(avatar)}
                                    onKeyPress={(e) => e.key === 'Enter' && setSelectedAvatar(avatar)}
                                    className={`h-16 w-16 rounded-full cursor-pointer transition-transform transform hover:scale-110 flex items-center justify-center text-4xl ${selectedAvatar.emoji === avatar.emoji ? 'ring-4 ring-offset-2 ring-offset-[#211a6e]' : ''}`}
                                    style={{ backgroundColor: `#${avatar.color}` }}
                                >
                                    {avatar.emoji}
                                </span>
                            ))}
                        </div>
                    </div>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-700">
                    <h2 className="text-xl font-bold text-center mb-4" style={{color: brandColors.wisteria}}>Settings</h2>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between bg-[#150e60] p-4 rounded-lg">
                            <label htmlFor="emailOptIn" className="text-gray-300">Receive email updates</label>
                            <input
                                id="emailOptIn"
                                type="checkbox"
                                checked={emailOptIn}
                                onChange={e => setEmailOptIn(e.target.checked)}
                                className="h-5 w-5 rounded bg-gray-700 border-gray-500 text-purple-500 focus:ring-purple-600"
                                style={{color: brandColors.palatinateBlue}}
                            />
                        </div>

                        {!userData?.leaderboardOptIn && (
                            <div className="bg-[#150e60] p-4 rounded-lg text-center">
                                <p className="text-gray-300 mb-3">Join the public leaderboard to see how you stack up against other challengers and get 10 bonus points!</p>
                                <button
                                    onClick={handleJoinLeaderboard}
                                    disabled={isJoining}
                                    style={{backgroundColor: brandColors.wisteria, color: brandColors.federalBlue}}
                                    className="font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {isJoining ? 'Joining...' : 'Join Leaderboard & Get 10 Points'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end pt-8">
                    <button 
                        type="submit" 
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{backgroundColor: brandColors.palatinateBlue}} 
                        className="hover:opacity-80 disabled:opacity-50 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EditUserModal = ({ user, onClose, onSave }) => {
    const [newEmail, setNewEmail] = useState(user.email);
    const [isSaving, setIsSaving] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        await onSave(user.id, newEmail);
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div ref={modalRef} className="relative max-w-lg w-full bg-[#211a6e] rounded-xl shadow-2xl p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-4">Edit User: {user.displayName}</h3>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="user-email" className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                            <input
                                id="user-email"
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                required
                                className="w-full p-2 bg-[#150e60] border border-gray-700 rounded-md text-white"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-4 mt-6">
                         <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            style={{ backgroundColor: brandColors.palatinateBlue }}
                            className="hover:opacity-80 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg"
                        >
                            {isSaving ? 'Saving...' : 'Save Email'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const UserAuditModal = ({ user, onClose, db, appId, userChallengeAttempts }) => {
    // const [userSubmissions, setUserSubmissions] = useState([]); // REMOVED
    const [userBonusSubmissions, setUserBonusSubmissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Now only loading bonus
    const modalRef = useRef(null);

    // CRITICAL FIX: This useEffect no longer fetches challenge data.
    // It ONLY fetches public bonus submissions, which is all it needs.
    // The main challenge attempts are now 100% supplied by the 'userChallengeAttempts' prop.
    useEffect(() => {
        const fetchBonusData = async () => {
            if (!user || !db || !appId) return;
            setIsLoading(true);
            setUserBonusSubmissions([]); // Clear old data

            // Fetch bonus submissions (public) - This should succeed.
            try {
                const bonusQuery = query(
                    collection(db, `artifacts/${appId}/public/data/bonusSubmissions`),
                    where("userId", "==", user.id)
                );
                const bonusSnapshot = await getDocs(bonusQuery);
                const bonusData = bonusSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUserBonusSubmissions(bonusData);
            } catch (error) {
                console.error("CRITICAL AUDIT ERROR: Failed to fetch public bonus submissions:", error);
                // setUserBonusSubmissions([]); // Already empty
            }

            setIsLoading(false);
        };

        fetchBonusData();
    }, [user, db, appId]);


const {
        basePoints,
        challengePointsBreakdown,
        totalChallengePoints,
        bonusPointsBreakdown,
        totalBonusPoints,
        calculatedTotalPoints,
        storedTotalPoints,
        discrepancy
    } = useMemo(() => {
        const bp = user.leaderboardOptIn ? 10 : 0;
        const stored = user.totalPoints || 0;

        // CRITICAL FIX: Ensure userChallengeAttempts defaults to empty array if undefined
        const safeChallengeAttempts = userChallengeAttempts || [];

        // Calculate challenge points - NOW USING PROPS
        const bestScores = new Map();

        // 1. Process all attempts (received via props)
        safeChallengeAttempts.forEach(sub => { // <-- Now uses safe array
            const challengeId = sub.challengeId || sub.id; 
            if (!challengeId) return; // FIX: Skip if challengeId is missing
            
            // CRITICAL FIX: Use the highest available score from EITHER scoreAwarded or score (from legacy)
            const scoreAwarded = sub.scoreAwarded !== undefined ? sub.scoreAwarded : (sub.score || 0);

            // Use 'greater than or equal' (>=) for best score calculation
            const currentBest = bestScores.get(challengeId) || 0;
            // --- THIS IS FIX #1 ---
            if (scoreAwarded >= currentBest) { 
                bestScores.set(challengeId, scoreAwarded);
            }
        });

        // 2. Legacy submissions are NO LONGER needed here,
        // because 'safeChallengeAttempts' (from collectionGroup) already includes them.
        
        const cpb = Array.from(bestScores.entries()).map(([id, score]) => ({ id, score }));
        const tcp = cpb.reduce((sum, item) => sum + item.score, 0);
        // ... (rest of useMemo remains the same)
        const bpb = userBonusSubmissions
            // FIX: Ensure status check is case-insensitive
            .filter(sub => sub.status && sub.status.toLowerCase() === 'approved')
            .map(sub => ({ name: sub.actionName, points: sub.pointsAwarded || 0 }));
        const tbp = bpb.reduce((sum, item) => sum + item.points, 0);
        
        const calculated = bp + tcp + tbp;
        const disc = stored - calculated;

        return {
            basePoints: bp,
            challengePointsBreakdown: cpb,
            totalChallengePoints: tcp,
            bonusPointsBreakdown: bpb,
            totalBonusPoints: tbp,
            calculatedTotalPoints: calculated,
            storedTotalPoints: stored,
            discrepancy: disc
        };
    }, [user, userChallengeAttempts, userBonusSubmissions]); // REMOVED userSubmissions dependency

 
    const discrepancyColor = discrepancy === 0 ? 'text-green-400' : 'text-red-400';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div ref={modalRef} className="relative max-w-2xl w-full bg-[#211a6e] rounded-xl shadow-2xl p-6 border border-gray-700 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-bold text-white mb-4">Points Audit: {user.displayName}</h3>
                {isLoading ? (
                    <Spinner />
                ) : (
                    <div className="space-y-6">
                        {/* Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-[#150e60] p-4 rounded-lg text-center">
                                <p className="text-sm text-gray-400">Stored Total</p>
                                <p className="text-2xl font-bold">{storedTotalPoints}</p>
                            </div>
                            <div className="bg-[#150e60] p-4 rounded-lg text-center">
                                <p className="text-sm text-gray-400">Calculated Total</p>
                                <p className="text-2xl font-bold">{calculatedTotalPoints}</p>
                            </div>
                            <div className="bg-[#150e60] p-4 rounded-lg text-center">
                                <p className="text-sm text-gray-400">Discrepancy</p>
                                <p className={`text-2xl font-bold ${discrepancyColor}`}>{discrepancy}</p>
                            </div>
                        </div>

                        {/* Breakdown */}
                        <div className="space-y-4">
                            {/* Base Points */}
                            <div>
                                <h4 className="font-bold text-lg" style={{ color: brandColors.wisteria }}>Base Points</h4>
                                <div className="bg-[#150e60] p-3 rounded-md mt-2">
                                    <div className="flex justify-between">
                                        <span>Leaderboard Opt-In</span>
                                        <span className="font-medium">{basePoints} pts</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Challenge Points */}
                            <div>
                                <h4 className="font-bold text-lg" style={{ color: brandColors.wisteria }}>Challenge Points (Total: {totalChallengePoints})</h4>
                                <div className="bg-[#150e60] p-3 rounded-md mt-2 space-y-2 max-h-40 overflow-y-auto">
                                    {challengePointsBreakdown.length > 0 ? challengePointsBreakdown.map(item => (
                                        <div key={item.id} className="flex justify-between text-sm">
                                            <span>Challenge ID: {item.id ? item.id.substring(0, 8) : 'N/A'}...</span>
                                            <span className="font-medium">{item.score} pts</span>
                                        </div>
                                    )) : <p className="text-sm text-gray-400">No challenge points found.</p>}
                                </div>
                            </div>

                            {/* Bonus Points */}
                            <div>
                                <h4 className="font-bold text-lg" style={{ color: brandColors.wisteria }}>Bonus Points (Total: {totalBonusPoints})</h4>
                                <div className="bg-[#150e60] p-3 rounded-md mt-2 space-y-2 max-h-40 overflow-y-auto">
                                    {bonusPointsBreakdown.length > 0 ? bonusPointsBreakdown.map((item, index) => (
                                        <div key={index} className="flex justify-between text-sm">
                                            <span>{item.name}</span>
                                            <span className="font-medium">{item.points} pts</span>
                                        </div>
                                    )) : <p className="text-sm text-gray-400">No approved bonus points.</p>}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 mt-6">
                            <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg">
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


const UsersPanel = ({ allUsers, onAdminPasswordReset, onUpdateUser, db, appId, allChallengeSubmissionsByUser }) => {
    const [editingUser, setEditingUser] = useState(null);
    const [auditingUser, setAuditingUser] = useState(null);

    const handleSaveUser = async (userId, newEmail) => {
        const success = await onUpdateUser(userId, newEmail);
        if (success) {
            setEditingUser(null);
        }
    };

    return (
        <div>
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Registered Users ({allUsers.length})</h3>
            <div className="bg-gray-900 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-black/20">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Points</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Badges</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email Opt-In</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Registered</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {allUsers.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)).map(user => {
                            const userAvatarObject = findAvatarFromUrl(user.avatar);
                            return (
                                <tr key={user.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                {userAvatarObject ? (
                                                    <span className="h-10 w-10 rounded-full flex items-center justify-center text-2xl" style={{backgroundColor: `#${userAvatarObject.color}`}}>{userAvatarObject.emoji}</span>
                                                ) : (
                                                    <img className="h-10 w-10 rounded-full" src={user.avatar} alt={`${user.displayName}'s avatar`} />
                                                )}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-white">{user.displayName}</div>
                                                <div className="text-sm text-gray-400">{user.leaderboardOptIn ? 'Public' : 'Private'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{user.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{user.totalPoints || 0}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{(user.badges || []).length}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{user.emailOptIn ? 'Yes' : 'No'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                        {user.createdAt ? user.createdAt.toDate().toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex flex-col sm:flex-row sm:space-x-2">
                                            <button
                                                onClick={() => setEditingUser(user)}
                                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-xs"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => onAdminPasswordReset(user.email)}
                                                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-1 px-3 rounded text-xs mt-1 sm:mt-0"
                                            >
                                                Reset Password
                                            </button>
                                            <button
                                                onClick={() => setAuditingUser(user)}
                                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-1 px-3 rounded text-xs mt-1 sm:mt-0"
                                            >
                                                Audit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSave={handleSaveUser}
                />
            )}
            {/* CRITICAL FIX: Ensure allChallengeSubmissionsByUser exists before rendering modal */}
            {auditingUser && allChallengeSubmissionsByUser && (
                <UserAuditModal
                    user={auditingUser}
                    onClose={() => setAuditingUser(null)}
                    db={db}
                    appId={appId}
                    // This lookup is now safe because allChallengeSubmissionsByUser exists
                    userChallengeAttempts={allChallengeSubmissionsByUser[auditingUser.id] || []} 
                />
            )}
        </div>
    );
};

const AnalyticsPanel = ({ allUsers, challenges, allChallengeSubmissions }) => {
    const totalPointsAwarded = useMemo(() => allUsers.reduce((sum, user) => sum + (user.totalPoints || 0), 0), [allUsers]);
    const usersWithPoints = useMemo(() => allUsers.filter(u => u.totalPoints > 0).length, [allUsers]);
    const averageScore = usersWithPoints > 0 ? (totalPointsAwarded / usersWithPoints).toFixed(2) : 0;

    const completionCounts = useMemo(() => {
        const counts = challenges.reduce((acc, c) => ({ ...acc, [c.id]: 0 }), {});
        allUsers.forEach(user => {
            if (user.badges && Array.isArray(user.badges)) {
                user.badges.forEach(badgeId => {
                    if (counts.hasOwnProperty(badgeId)) {
                        counts[badgeId]++;
                    }
                });
            }
        });
        return counts;
    }, [challenges, allUsers]);

    return (
        <div>
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Key Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white mb-8">
                <div className="bg-[#150e60] p-4 rounded-lg border border-gray-700 text-center">
                    <p className="text-3xl font-bold">{allUsers.length}</p>
                    <p className="text-gray-400">Total Users</p>
                </div>
                <div className="bg-[#150e60] p-4 rounded-lg border border-gray-700 text-center">
                    <p className="text-3xl font-bold">{totalPointsAwarded}</p>
                    <p className="text-gray-400">Total Points Awarded</p>
                </div>
                <div className="bg-[#150e60] p-4 rounded-lg border border-gray-700 text-center">
                    <p className="text-3xl font-bold">{averageScore}</p>
                    <p className="text-gray-400">Average Score</p>
                </div>
            </div>

            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Completions per Challenge</h3>
            <div className="bg-[#150e60] rounded-lg border border-gray-700 p-4">
                <ul className="space-y-2">
                    {challenges.map(challenge => (
                        <li key={challenge.id} className="flex justify-between items-center text-gray-300">
                            <span>{challenge.title}</span>
                            <span className="font-bold text-lg" style={{color: brandColors.wisteria}}>
                                {completionCounts[challenge.id] || 0}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

// --- Updated Component: AdminNav (Segment D) ---
const AdminNav = ({ adminPage, setAdminPage }) => (
    <div className="flex space-x-2 border-b-2 border-gray-700 mb-6 overflow-x-auto pb-1">
        <button onClick={() => setAdminPage('challenges')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'challenges' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'challenges' ? brandColors.wisteria : 'transparent'}}>Challenges</button>
        <button onClick={() => setAdminPage('bonus')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'bonus' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'bonus' ? brandColors.wisteria : 'transparent'}}>Bonus Points</button>
        <button onClick={() => setAdminPage('attempts')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'attempts' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'attempts' ? brandColors.wisteria : 'transparent'}}>Challenge History</button>
        <button onClick={() => setAdminPage('analytics')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'analytics' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'analytics' ? brandColors.wisteria : 'transparent'}}>Analytics</button>
        <button onClick={() => setAdminPage('broadcast')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'broadcast' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'broadcast' ? brandColors.wisteria : 'transparent'}}>Broadcast</button>
        <button onClick={() => setAdminPage('users')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'users' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'users' ? brandColors.wisteria : 'transparent'}}>Users</button>
        <button onClick={() => setAdminPage('publish')} className={`py-2 px-4 font-semibold whitespace-nowrap ${adminPage === 'publish' ? 'border-b-2 text-white' : 'text-gray-400'}`} style={{borderColor: adminPage === 'publish' ? brandColors.wisteria : 'transparent'}}>Publish Data</button>
    </div>
);

// --- Updated Component: AdminPanel (Segment D) ---
const AdminPanel = ({ allUsers, challenges, bonusActions, db, appId, showNotification, user, userData, allBonusSubmissions, onReview, functions, allChallengeSubmissions, onAdminPasswordReset, onUpdateUserEmail, allChallengeSubmissionsByUser }) => {
    const [adminPage, setAdminPage] = useState('bonus');

    const pendingBonusSubmissions = useMemo(() => {
        return allBonusSubmissions.filter(s => s.status && s.status.trim().toLowerCase() === 'pending');
    }, [allBonusSubmissions]);

    // New component to handle the Cloud Function call
    const PublishPanel = () => {
        const [isPublishing, setIsPublishing] = useState(false);
        const handlePublish = async () => {
            if (!functions) {
                showNotification('Functions service is not available.', 'error');
                return;
            }
            setIsPublishing(true);
            try {
                // Set 9 minute (540,000ms) timeout
                const publishLeaderboardBreakdown = httpsCallable(functions, 'publishLeaderboardBreakdown', { timeout: 540000 });
                const result = await publishLeaderboardBreakdown({});
                showNotification(result.data?.message || 'Leaderboard breakdown published successfully!', 'success');
            } catch (error) {
                console.error('Error publishing breakdown:', error);
                showNotification(error.message || 'Failed to publish breakdown.', 'error');
            }
            setIsPublishing(false);
        };

        return (
            <div className="bg-[#150e60] p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Publish Public Leaderboard Data</h3>
                <p className="text-gray-400 mb-6">This action securely recalculates the detailed score breakdown for all users and publishes the report to a public collection, ensuring non-admin users can view the detailed scoring when they expand a row on the Leaderboard.</p>
                <button
                    onClick={handlePublish}
                    disabled={isPublishing}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                >
                    {isPublishing ? 'Publishing...' : 'Run Score Calculation & Publish'}
                </button>
            </div>
        );
    };

    return (
        <div className="bg-[#211a6e] p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Admin Panel</h2>
            <AdminNav adminPage={adminPage} setAdminPage={setAdminPage} />
            
            {/* Render new Publish Panel */}
            {adminPage === 'publish' && <PublishPanel />}

            {adminPage === 'challenges' && challenges.map(challenge => <ChallengeEditor key={challenge.id} challenge={challenge} db={db} appId={appId} showNotification={showNotification} />)}
            {adminPage === 'bonus' && <BonusActionEditor bonusActions={bonusActions} showNotification={showNotification} pendingSubmissions={pendingBonusSubmissions} allSubmissions={allBonusSubmissions} onReview={onReview} functions={functions} />}
            {adminPage === 'analytics' && <AnalyticsPanel allUsers={allUsers} challenges={challenges} allChallengeSubmissions={allChallengeSubmissions} />}
            {adminPage === 'attempts' && <ChallengeAttemptsPanel allSubmissions={allChallengeSubmissions} allUsers={allUsers} challenges={challenges} />}
            {adminPage === 'broadcast' && <BroadcastPanel db={db} appId={appId} showNotification={showNotification} />}
            {adminPage === 'users' && <UsersPanel allUsers={allUsers} onAdminPasswordReset={onAdminPasswordReset} onUpdateUser={onUpdateUserEmail} db={db} appId={appId} allChallengeSubmissionsByUser={allChallengeSubmissionsByUser} />}
        </div>
    );
};

const ChallengeAttemptsPanel = ({ allSubmissions, allUsers, challenges }) => {
    const enrichedSubmissions = useMemo(() => {
        return allSubmissions.map(sub => {
            const user = allUsers.find(u => u.id === sub.userId);
            const challenge = challenges.find(c => c.id === sub.challengeId);
            return {
                ...sub,
                userDisplayName: user?.displayName || 'Unknown User',
                challengeTitle: challenge?.title || 'Unknown Challenge',
            };
        }).sort((a, b) => (b.submittedAt?.toMillis() || 0) - (a.submittedAt?.toMillis() || 0));
    }, [allSubmissions, allUsers, challenges]);

    return (
        <div>
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Challenge Attempt History ({enrichedSubmissions.length})</h3>
            <div className="bg-gray-900 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                     <thead className="bg-black/20">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Challenge</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Score</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                        </tr>
                    </thead>
                     <tbody className="divide-y divide-gray-800">
                        {enrichedSubmissions.map((sub, index) => (
                            <tr key={`${sub.id}-${index}`}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{sub.userDisplayName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{sub.challengeTitle}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{sub.scoreAwarded}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{sub.submittedAt ? new Date(sub.submittedAt.seconds * 1000).toLocaleString() : 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}


const BroadcastPanel = ({ db, appId, showNotification }) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    const handleSend = async () => {
        if (!title || !message) {
            showNotification("Title and message are required.", "error");
            return;
        }
        setIsSending(true);
        try {
            await addDoc(collection(db, `artifacts/${appId}/public/data/broadcasts`), {
                title,
                message,
                createdAt: serverTimestamp()
            });
            showNotification("Broadcast sent!", "success");
            setTitle('');
            setMessage('');
        } catch (error) {
            console.error("Error sending broadcast:", error);
            showNotification(`Error: ${error.message}`, "error");
        }
        setIsSending(false);
    };

    return (
        <div>
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Send a Broadcast Message</h3>
            <p className="text-sm text-gray-400 mb-4">This message will appear as a banner on the homepage for all users.</p>
            <div className="space-y-4">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full bg-gray-900 p-2 rounded-md border border-gray-600" />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message content..." rows="4" className="w-full bg-gray-900 p-2 rounded-md border border-gray-600" />
            </div>
            <div className="flex justify-end mt-4">
                <button onClick={handleSend} disabled={isSending} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">
                    {isSending ? 'Sending...' : 'Send Broadcast'}
                </button>
            </div>
        </div>
    );
};

const InfoTile = ({ icon, title, children }) => (
    <div className="bg-[#211a6e] p-6 rounded-lg border border-gray-700 flex flex-col">
        <div className="flex items-center mb-4 flex-shrink-0">
            <div className="flex-shrink-0 mr-4">{icon}</div>
            <h3 className="text-2xl font-bold text-white">{title}</h3>
        </div>
        {children}
    </div>
);
const AccordionItem = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="bg-[#211a6e] rounded-lg border border-gray-700">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-4 font-bold text-lg text-left">
                <span>{title}</span>
                <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}><ChevronDown /></span>
            </button>
            {isOpen && <div className="p-4 pt-0 text-gray-300">{children}</div>}
        </div>
    );
};


const WhyJoinCard = ({ icon, title, description }) => (
    <div className="bg-transparent flex flex-col items-center">
        <div style={{color: brandColors.wisteria}} className="mb-4">{icon}</div>
        <h3 className="text-xl font-bold mt-4 mb-2 text-white">{title}</h3>
        <p className="text-gray-300">{description}</p>
    </div>
);

const FinalCTA = ({ onJoin, user, userData, onNavigate }) => {
    const userIsAnonymous = !user || user.isAnonymous;

    return (
        <div className="max-w-4xl mx-auto text-center py-16">
            <div className="bg-gradient-to-r from-[#211a6e] via-[#473dc6] to-[#211a6e] rounded-3xl p-8 md:p-12 shadow-lg border-2 border-[#473dc6]">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Ready to secure your digital future?
                </h2>
                <p className="text-gray-300 text-lg mb-8">
                    One last challenge could make you this year's CybHER Champion. Jump in now!
                </p>
                {userIsAnonymous ? (
                    <button
                        onClick={() => onJoin('register')}
                        className="bg-[#caa3d6] hover:bg-[#a788ba] text-[#150e60] font-bold py-3 px-8 rounded-lg shadow-md text-lg transition-colors"
                    >
                        Join the Challenge
                    </button>
                ) : (
                    <button
                        onClick={() => onNavigate('dashboard')}
                        className="bg-[#caa3d6] hover:bg-[#a788ba] text-[#150e60] font-bold py-3 px-8 rounded-lg shadow-md text-lg transition-colors"
                    >
                        View Challenges
                    </button>
                )}
                
                <div className="mt-4">
                  <a
                    href="https://newsletter.createherfest.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ borderColor: brandColors.wisteria, color: brandColors.wisteria }}
                    className="inline-block bg-transparent border-2 font-bold py-3 px-8 rounded-lg transition-colors hover:bg-[#caa3d620]"
                  >
                    Subscribe to our Newsletter
                  </a>
                </div>

                <p className="mt-8 text-gray-400 text-sm">
                    Questions? Email <a href="mailto:adriann@createherfest.com" className="underline" style={{color: brandColors.wisteria}}>adriann@createherfest.com</a>
                </p>
            </div>
        </div>
    );
};

const NewBadgeModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
    <div className="relative max-w-sm w-full bg-[#211a6e] rounded-xl shadow-2xl p-6 border border-gray-700 text-center">
      <div className="mb-4">
        <SparkleIcon className="h-12 w-12 text-yellow-400 mx-auto" />
      </div>
      <h3 className="text-lg font-bold text-white mb-2">New Badge Unlocked!</h3>
      <p className="text-gray-300 text-sm mb-6">Congratulations! You've earned a new badge for your collection.</p>
      <button 
        onClick={onClose} 
        style={{backgroundColor: brandColors.palatinateBlue, color: brandColors.isabelline}} 
        className="hover:opacity-80 font-bold py-2 px-6 rounded-lg transition-colors w-full"
      >
        View
      </button>
    </div>
  </div>
);


const BadgeModal = ({ imageUrl, title, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="relative max-w-lg w-full bg-[#150e60] p-4 rounded-xl border-2" style={{borderColor: brandColors.wisteria}} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-4 -right-4 bg-gray-800 rounded-full p-2 text-white hover:bg-gray-700 z-10">
          <XIcon/>
        </button>
        <img src={imageUrl} alt={`Enlarged badge for ${title}`} className="w-full h-auto rounded-lg" />
        <p className="text-center font-bold text-white mt-4 text-lg">{title}</p>
      </div>
    </div>
  );
};


const Footer = ({ onNavigate }) => (
  <footer className="bg-[#211a6e] text-gray-300 p-8 text-center mt-12">
    <div className="max-w-5xl mx-auto">
      <div className="mb-4">
        <CreateHerFestLogo className="h-16 mx-auto mb-2" />
        <h3 className="font-bold" style={{color: brandColors.wisteria}}>CybHER Challenge</h3>
      </div>
      <p className="text-gray-400 mb-2">
        Part of CreateHER Fest's mission to empower innovators in emerging tech and beyond!
      </p>
      <p className="text-gray-600 text-xs">
        &copy; 2025 CreateHer Fest. Building safer digital futures together.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-x-4 text-sm">
        <button onClick={() => onNavigate('privacy')} style={{color: brandColors.wisteria}} className="underline">Privacy Policy</button>
        <span>|</span>
        <a href="https://createherfest.com" target="_blank" rel="noopener noreferrer" style={{color: brandColors.wisteria}} className="underline">Visit CreateHERFest.com</a>
      </div>
    </div>
  </footer>
);


const MessageModal = ({ message, type, onClose }) => {
    const icons = {
        success: <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto" />,
        error: <AlertTriangleIcon className="h-12 w-12 text-red-400 mx-auto" />,
        info: <InfoIcon className="h-12 w-12 text-blue-400 mx-auto" />,
    };

    const titles = {
        success: 'Success!',
        error: 'Oops!',
        info: 'Heads up!',
    };
    
    const icon = icons[type] || icons.info;
    const title = titles[type] || titles.info;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="relative max-w-sm w-full bg-[#211a6e] rounded-xl shadow-2xl p-6 border border-gray-700 text-center">
                <div className="mb-4">{icon}</div>
                <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
                <p className="text-gray-300 text-sm mb-6">{message}</p>
                <button 
                    onClick={onClose} 
                    style={{backgroundColor: brandColors.palatinateBlue, color: brandColors.isabelline}} 
                    className="hover:opacity-80 font-bold py-2 px-6 rounded-lg transition-colors w-full"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

const ImageZoomModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 bg-gray-800 rounded-full p-2 text-white hover:bg-gray-700 z-10">
        <XIcon/>
      </button>
      <div className="relative w-full h-full flex items-center justify-center">
        <img src={imageUrl} alt="Zoomed artifact" className="max-w-[90vw] max-h-[90vh] object-contain" />
      </div>
    </div>
  );
};

// --- Updated & New Components for Bonus Points Feature ---

const BonusPointsPage = ({ user, userData, bonusActions, bonusSubmissions, allBonusSubmissions, onSubmit, showNotification }) => {
  const [formAnswers, setFormAnswers] = useState({});
  const [openActionId, setOpenActionId] = useState(null);

  const handleToggleAction = (actionId) => {
    setOpenActionId(prevId => (prevId === actionId ? null : actionId));
  };

  const handleAnswerChange = (actionId, fieldName, value) => {
    setFormAnswers(prev => ({
      ...prev,
      [actionId]: {
        ...prev[actionId],
        [fieldName]: value
      }
    }));
  };

  const renderField = (field, actionId) => {
    const actionAnswers = formAnswers[actionId] || {};
    const value = actionAnswers[field.name] || '';

    if (field.condition) {
      const conditionValue = actionAnswers[field.condition.field];
      if (conditionValue !== field.condition.value) {
        return null;
      }
    }

    switch (field.type) {
      case 'text':
      case 'url':
        return (
          <input
            type={field.type === 'url' ? 'url' : 'text'}
            value={value}
            onChange={e => handleAnswerChange(actionId, field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="w-full p-2 bg-[#150e60] border border-gray-700 rounded-md text-white"
          />
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={e => handleAnswerChange(actionId, field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={field.rows || 3}
            className="w-full p-2 bg-[#150e60] border border-gray-700 rounded-md text-white"
          />
        );
      case 'select':
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={e => handleAnswerChange(actionId, field.name, e.target.value)}
            required={field.required}
            className="w-full p-2 bg-[#150e60] border border-gray-700 rounded-md text-white"
          >
            <option value="">Select an option...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      default:
        return <p className="text-red-500">Unknown field type: {field.type}</p>;
    }
  };

  const renderSubmissionForm = (action, isResubmission = false) => {
    let schema;
    try {
        schema = action.formSchema ? JSON.parse(action.formSchema) : { fields: [] };
    } catch {
        schema = { fields: [] };
    }
    
    return (
      <form onSubmit={e => { e.preventDefault(); onSubmit(action, formAnswers[action.id] || {}); }}>
        {isResubmission && <p className="text-yellow-300 text-sm mb-4 text-center">Your previous submission was not approved. Please review the requirements and try again.</p>}
        <div className="space-y-4">
            {schema.fields.map(field => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-400 mb-1">{field.label}</label>
                {renderField(field, action.id)}
              </div>
            ))}
        </div>
        <button
          type="submit"
          style={{ backgroundColor: brandColors.palatinateBlue }}
          className="w-full hover:opacity-80 text-white font-bold py-2 px-4 rounded-lg mt-6"
        >
          {isResubmission ? 'Resubmit' : 'Submit'}
        </button>
      </form>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold" style={{ color: brandColors.wisteria }}>
          Smart Security Actions
        </h1>
        <p className="text-gray-300 mt-2">Complete these actions to earn bonus points and enhance your digital security!</p>
      </div>

      <div className="space-y-4">
        {bonusActions.sort((a,b) => (a.order || 0) - (b.order || 0)).map(action => {
            const isOpen = openActionId === action.id;
            const existingSubmission = bonusSubmissions.find(sub => sub.actionId === action.id);
            const publicSubmissionDetails = existingSubmission ? allBonusSubmissions.find(s => s.id === existingSubmission.publicSubmissionId) : null;
            
            return (
                <div key={action.id} className="bg-[#211a6e] rounded-lg border border-gray-700 overflow-hidden transition-all duration-300">
                    <button onClick={() => handleToggleAction(action.id)} className="w-full flex justify-between items-center p-4 text-left">
                        <span className="font-bold text-lg text-white flex items-center gap-2">
                          {action.name}
                          {existingSubmission?.status === 'Approved' && <CheckCircleIcon className="h-5 w-5 text-green-400" />}
                          {existingSubmission?.status === 'Pending' && <HourglassIcon />}
                          {existingSubmission?.status === 'Rejected' && <AlertTriangleIcon className="h-5 w-5 text-red-400" />}
                        </span>
                        <div className="flex items-center">
                            <span className="text-base font-bold mr-4" style={{ color: brandColors.wisteria }}>
                              {action.points} Points {action.extraPoints > 0 && `(+${action.extraPoints} Bonus)`}
                            </span>
                            <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                <ChevronDown />
                            </span>
                        </div>
                    </button>

                    {isOpen && (
                        <div className="p-4 pt-0">
                            <div className="border-t border-gray-700 my-4"></div>
                            
                            {action.imageUrl && (
                                <img 
                                    src={action.imageUrl} 
                                    alt="Action illustration" 
                                    className="w-full max-w-md mx-auto mb-6 rounded-lg"
                                />
                            )}

                            <div className="mb-6 text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(action.instructions) }} />
                            
                            <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
                                { !existingSubmission || existingSubmission.status === 'Rejected' ? (
                                    renderSubmissionForm(action, existingSubmission?.status === 'Rejected')
                                ) : (
                                    <div className={`p-4 rounded-lg text-center ${
                                        existingSubmission.status === 'Approved' ? 'bg-green-500/20 text-green-300' :
                                        'bg-yellow-500/20 text-yellow-300'
                                    }`}>
                                        <div className="font-bold flex items-center justify-center">
                                            {existingSubmission.status === 'Pending' && <HourglassIcon/>}
                                            {existingSubmission.status === 'Approved' && <CheckCircleIcon className="h-5 w-5 mr-1"/>}
                                            Status: {existingSubmission.status}
                                        </div>
                                        {existingSubmission.status === 'Approved' && publicSubmissionDetails?.pointsAwarded > 0 && (
                                            <p className="text-sm mt-2">✨ You were awarded {publicSubmissionDetails.pointsAwarded} points! ✨</p>
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>
                    )}
                </div>
            );
        })}
      </div>
    </div>
  );
};


const BonusActionEditor = ({ bonusActions, showNotification, functions, pendingSubmissions, allSubmissions, onReview }) => {
  const [newAction, setNewAction] = useState({ name: '', instructions: '', points: 10, extraPoints: 0, order: 100, imageUrl: '', validationType: 'manual', formSchema: '{\n  "fields": []\n}' });
  const [editingAction, setEditingAction] = useState(null);
  const [jsonError, setJsonError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [reviewPoints, setReviewPoints] = useState({});
  const [expandedSubId, setExpandedSubId] = useState(null);

  useEffect(() => {
    const initialPoints = {};
    if (pendingSubmissions) {
      pendingSubmissions.forEach(sub => {
        if (reviewPoints[sub.id] === undefined) {
          const action = bonusActions.find(a => a.id === sub.actionId);
          initialPoints[sub.id] = action ? action.points + (action.extraPoints || 0) : 0;
        }
      });
    }
    if (Object.keys(initialPoints).length > 0) {
        setReviewPoints(prev => ({ ...prev, ...initialPoints }));
    }
  }, [pendingSubmissions, bonusActions]);
  
  const handleNewActionChange = (field, value) => { setNewAction(prev => ({ ...prev, [field]: value })); };
  const handleEditingActionChange = (field, value) => { setEditingAction(prev => ({ ...prev, [field]: value })); };

  const handleCreateAction = async (e) => {
    e.preventDefault();
    setJsonError('');
    try { JSON.parse(newAction.formSchema); } catch { setJsonError('Form Schema JSON is invalid.'); return; }
    setIsSaving(true);
    try {
        const createBonusAction = httpsCallable(functions, 'createBonusAction');
        await createBonusAction({ ...newAction, points: Number(newAction.points) || 10, extraPoints: Number(newAction.extraPoints) || 0, order: Number(newAction.order) || 100 });
        showNotification('New bonus action created!', 'success');
        setNewAction({ name: '', instructions: '', points: 10, extraPoints: 0, order: 100, imageUrl: '', validationType: 'manual', formSchema: '{"fields": []}' });
    } catch (error) { console.error('Error creating action:', error); showNotification(`Error: ${error.message}`, 'error'); }
    setIsSaving(false);
  };

  const handleUpdateAction = async (e) => {
    e.preventDefault();
    if (!editingAction) return;
    setJsonError('');
    try { JSON.parse(editingAction.formSchema); } catch { setJsonError('Form Schema JSON is invalid.'); return; }
    setIsSaving(true);
    try {
      const updateBonusAction = httpsCallable(functions, 'updateBonusAction');
      await updateBonusAction({ actionId: editingAction.id, data: { name: editingAction.name, instructions: editingAction.instructions, points: Number(editingAction.points), extraPoints: Number(editingAction.extraPoints), order: Number(editingAction.order), imageUrl: editingAction.imageUrl || '', validationType: editingAction.validationType || 'manual', formSchema: editingAction.formSchema } });
      showNotification('Bonus action updated!', 'success');
      setEditingAction(null);
    } catch (error) { console.error('Error updating action:', error); showNotification(`Error: ${error.message}`, 'error'); }
    setIsSaving(false);
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Are you sure you want to delete this bonus action? This cannot be undone.')) return;
    if (!functions) { showNotification('Functions service not available. Cannot delete securely.', 'error'); return; }
    setIsSaving(true);
    try {
      const deleteBonusAction = httpsCallable(functions, 'deleteBonusAction');
      await deleteBonusAction({ actionId: actionId });
      showNotification('Bonus action deleted!', 'success');
    } catch (error) { console.error('Error deleting action:', error); showNotification(error.message || 'An error occurred while deleting.', 'error'); } finally { setIsSaving(false); }
  };

  const handleEditAction = (action) => { setJsonError(''); setEditingAction({ ...action, imageUrl: action.imageUrl || '', validationType: action.validationType || 'manual', formSchema: action.formSchema || '{"fields": []}' }); };

  const renderActionForm = (actionData, handler, onSubmit, submitText, isEditing = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <input value={actionData.name || ''} onChange={(e) => handler('name', e.target.value)} placeholder="Action Name" required className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/>
        <textarea value={actionData.instructions || ''} onChange={(e) => handler('instructions', e.target.value)} placeholder="Instructions" rows="4" required className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/>
        <input value={actionData.imageUrl || ''} onChange={(e) => handler('imageUrl', e.target.value)} placeholder="Optional Image URL" className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-400 mb-1">Points</label><input type="number" value={actionData.points || ''} onChange={(e) => handler('points', e.target.value)} placeholder="e.g. 50" required className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/></div>
            <div><label className="block text-sm font-medium text-gray-400 mb-1">Bonus Points</label><input type="number" value={actionData.extraPoints || ''} onChange={(e) => handler('extraPoints', e.target.value)} placeholder="e.g. 10" className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/></div>
            <div><label className="block text-sm font-medium text-gray-400 mb-1">Display Order</label><input type="number" value={actionData.order || ''} onChange={(e) => handler('order', e.target.value)} placeholder="e.g. 100" required className="w-full bg-[#150e60] p-2 rounded border border-gray-600 text-white"/></div>
        </div>
        <div>
            <label className="text-sm font-medium text-gray-400">Form Schema (JSON)</label>
            <textarea value={actionData.formSchema || ''} onChange={(e) => handler('formSchema', e.target.value)} rows="8" className="font-mono text-sm w-full bg-black p-2 rounded border border-gray-600 text-white" />
            {jsonError && <p className="text-red-400 text-xs mt-1">{jsonError}</p>}
        </div>
        <div className="flex justify-end space-x-2">
            {isEditing && <button type="button" onClick={() => setEditingAction(null)} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>}
            <button type="submit" disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">{submitText}</button>
        </div>
    </form>
  );

  return (
    <div>
        <div className="mb-8 p-4 bg-[#150e60] rounded-lg border border-gray-700">
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>{editingAction ? 'Edit Bonus Action' : 'Create New Bonus Action'}</h3>
            {editingAction ? renderActionForm(editingAction, handleEditingActionChange, handleUpdateAction, 'Update Action', true) : renderActionForm(newAction, handleNewActionChange, handleCreateAction, 'Create Action')}
        </div>
        <div className="space-y-4">
            <h3 className="text-xl font-bold" style={{color: brandColors.wisteria}}>Existing Actions</h3>
            {bonusActions.map(action => (<div key={action.id} className="bg-[#150e60] p-4 rounded-lg border border-gray-700 flex justify-between items-center"><div><p className="font-bold text-white">{action.name}</p><p className="text-sm text-gray-400">{action.points} pts (+{action.extraPoints || 0} bonus)</p></div><div className="flex space-x-2"><button onClick={() => handleEditAction(action)} className="bg-blue-800 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm">Edit</button><button onClick={() => handleDeleteAction(action.id)} className="bg-red-800 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm">Delete</button></div></div>))}
        </div>
        <div className="mt-8 pt-8 border-t border-gray-700">
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Review Submissions ({pendingSubmissions.length} pending)</h3>
            <div className="space-y-4">{pendingSubmissions.length === 0 && <p className="text-gray-400">No pending submissions to review.</p>}{pendingSubmissions.map(sub => (<div key={sub.id} className="bg-[#150e60] p-4 rounded-lg border border-gray-700"><div className="flex justify-between items-start"><div><h4 className="font-bold">{sub.actionName}</h4><p className="text-sm text-gray-400">From: {sub.userDisplayName}</p></div><span className="text-xs text-gray-500">{new Date(sub.submittedAt.seconds * 1000).toLocaleString()}</span></div><div className="mt-3 bg-gray-900 p-3 rounded-md text-sm">{typeof sub.answer === 'object' && sub.answer !== null ? (Object.entries(sub.answer).map(([key, value]) => <p key={key}><strong>{key}:</strong> {String(value)}</p>)) : (<p><strong>Answer:</strong> {String(sub.answer)}</p>)}</div><div className="flex justify-end items-center space-x-2 mt-3"><input type="number" value={reviewPoints[sub.id] || ''} onChange={(e) => setReviewPoints(prev => ({...prev, [sub.id]: Number(e.target.value)}))} className="bg-gray-900 p-1 rounded-md border border-gray-600 w-20 text-center" /><button onClick={() => onReview(sub, false, 0)} disabled={isSaving} className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm">Reject</button><button onClick={() => onReview(sub, true, reviewPoints[sub.id] || 0)} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm">Approve</button></div></div>))}</div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-700">
            <h3 className="text-xl font-bold mb-4" style={{color: brandColors.wisteria}}>Bonus Points History</h3>
            <div className="bg-gray-900 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-black/20">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Action</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Points</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {allSubmissions.sort((a,b) => b.submittedAt.seconds - a.submittedAt.seconds).map(sub => (
                            <React.Fragment key={sub.id}>
                               <tr>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{sub.userDisplayName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{sub.actionName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            sub.status === 'Approved' ? 'bg-green-500/20 text-green-300' :
                                            sub.status === 'Rejected' ? 'bg-red-500/20 text-red-300' :
                                            'bg-yellow-500/20 text-yellow-300'
                                        }`}>
                                            {sub.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{sub.pointsAwarded || 0}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{new Date(sub.submittedAt.seconds * 1000).toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => setExpandedSubId(expandedSubId === sub.id ? null : sub.id)} className="text-indigo-400 hover:text-indigo-600">
                                            {expandedSubId === sub.id ? 'Hide' : 'View'}
                                        </button>
                                    </td>
                                </tr>
                                {expandedSubId === sub.id && (
                                    <tr className="bg-gray-800">
                                        <td colSpan="6" className="px-6 py-4">
                                            <div className="text-sm text-white">
                                                <h4 className="font-bold mb-2">Submission Details:</h4>
                                                <div className="bg-black/20 p-3 rounded-md prose prose-sm prose-invert max-w-none break-words">
                                                    {typeof sub.answer === 'object' && sub.answer !== null ? (
                                                        Object.entries(sub.answer).map(([key, value]) => <p key={key}><strong>{key}:</strong> {String(value)}</p>)
                                                    ) : (
                                                        <p><strong>Answer:</strong> {String(sub.answer)}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
  );
};


// --- Main App Component ---
export default function App() {
  const [auth, setAuth] = useState(null);
  const [db, setDb] = useState(null);
  const [functions, setFunctions] = useState(null);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [page, setPage] = useState('dashboard');
  const [challenges, setChallenges] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messageModal, setMessageModal] = useState({ message: '', type: '' });
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [initialModalView, setInitialModalView] = useState('register');
  const [modalBadge, setModalBadge] = useState(null);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);
  const [hasUnviewedBadges, setHasUnviewedBadges] = useState(false);
  const [showNewBadgeModal, setShowNewBadgeModal] = useState(false);
  const [bonusActions, setBonusActions] = useState([]);
  const [bonusSubmissions, setBonusSubmissions] = useState({});
  const [allBonusSubmissions, setAllBonusSubmissions] = useState([]);
  const [allChallengeSubmissions, setAllChallengeSubmissions] = useState([]); // This will hold public breakdowns for non-admins, raw attempts for admins
  const [hasUnviewedBonusContent, setHasUnviewedBonusContent] = useState(false);
  const appId = "chf-cybher-challenge";

  const allChallengeSubmissionsByUser = useMemo(() => {
    // This memo now processes the data from 'allChallengeSubmissions'
    // which is populated by different listeners based on user role.
    if (!allChallengeSubmissions || allChallengeSubmissions.length === 0) {
      return {};
    }

    const isStructuredReport = allChallengeSubmissions[0].challengeBreakdown !== undefined;

    if (isStructuredReport) {
      // Non-Admin: Group structured reports by user
      return allChallengeSubmissions.reduce((acc, report) => {
        if (report.userId && report.challengeBreakdown) {
            // Value is the array of score objects
            acc[report.userId] = report.challengeBreakdown; 
        }
        return acc;
      }, {});
    } else {
      // Admin: Group flat list of attempts by user (from collectionGroup)
      // Data is like: [ { userId: 'uA', challengeId: 'c1', ... }, { userId: 'uA', challengeId: 'c2', ... } ]
      return allChallengeSubmissions.reduce((acc, sub) => {
        const userId = sub.userId;
        if (!acc[userId]) acc[userId] = [];
        acc[userId].push(sub);
        return acc;
      }, {});
    }
  }, [allChallengeSubmissions]);


  const allBonusSubmissionsByUser = useMemo(() => {
      // This remains the same, as bonus submissions are always public.
      return allBonusSubmissions.reduce((acc, sub) => {
          const userId = sub.userId;
          if (!acc[userId]) acc[userId] = [];
          acc[userId].push(sub);
          return acc;
      }, {});
  }, [allBonusSubmissions]);
  // --- End Data Memos ---


  const pendingBonusSubmissions = useMemo(() => {
    return allBonusSubmissions.filter(s => s.status && s.status.trim().toLowerCase() === 'pending');
  }, [allBonusSubmissions]);

  const showMessage = (message, type) => { setMessageModal({ message, type }); };

  const handleReviewBonusSubmission = async (submission, isApproved, pointsToAward) => {
    if (!functions) {
      showMessage('Functions service is not available. Cannot review submission.', 'error');
      return;
    }

    try {
      const reviewSubmission = httpsCallable(functions, 'reviewBonusSubmission');
      await reviewSubmission({
        publicSubmissionId: submission.id,
        userId: submission.userId,
        actionId: submission.actionId,
        isApproved: isApproved,
        pointsToAward: isApproved ? pointsToAward : 0
      });
      showMessage(`Submission has been ${isApproved ? 'approved' : 'rejected'}.`, 'success');
    } catch (error) {
      console.error("Error calling reviewBonusSubmission function:", error);
      showMessage(error.message || 'An error occurred while reviewing.', 'error');
    }
  };

  const handleUpdateUserEmail = async (userId, newEmail) => {
    if (!functions) {
        showMessage('Functions service is not available.', 'error');
        return false;
    }
    const adminUpdateUserEmail = httpsCallable(functions, 'adminUpdateUserEmail');
    try {
        await adminUpdateUserEmail({ userId, newEmail });
        showMessage("User's email updated successfully.", 'success');
        return true;
    } catch (error) {
        console.error("Error updating user email:", error);
        showMessage(`Failed to update email: ${error.message}`, 'error');
        return false;
    }
  };

  const handleAdminPasswordReset = async (email) => {
    if (!auth) {
      showMessage("Authentication service is not available.", "error");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      showMessage(`A password reset email has been sent to ${email}.`, 'success');
    } catch (error) {
      console.error("Admin password reset error:", error);
      showMessage(`Failed to send reset email: ${error.message}`, 'error');
    }
  };

  const handleForgotPassword = async (email) => {
    if (!auth) throw new Error("Authentication service is unavailable.");
    await sendPasswordResetEmail(auth, email);
    const successMessage = `A password reset link has been sent to ${email}. Please allow a few minutes for it to arrive. Be sure to check your Promotions or Spam folders for an email with the subject line 'Reset your password for chf-cybher-challenge'. To ensure delivery, consider adding 'noreply@chf-cybher-challenge.firebaseapp.com' to your contacts. If you still do not see the email after 15 minutes, please contact support at adriann@createherfest.com for assistance.`;
    showMessage(successMessage, 'success');
  };

  useEffect(() => {
    const firebaseConfig = {
        apiKey: "AIzaSyCCe5vqyIT7-uI-lhbZe9qY2C5RpcqSLrA",
        authDomain: "chf-cybher-challenge.firebaseapp.com",
        projectId: "chf-cybher-challenge",
        storageBucket: "chf-cybher-challenge.appspot.com",
        messagingSenderId: "147405164797",
        appId: "1:147405164797:web:782ce0a0787619c244ee94",
        measurementId: "G-4KH3KTJDWJ"
    };
    
    try {
      const app = initializeApp(firebaseConfig);
      const authInstance = getAuth(app);
      const dbInstance = getFirestore(app);
      const functionsInstance = getFunctions(app, 'us-central1');
      setAuth(authInstance);
      setDb(dbInstance);
      setFunctions(functionsInstance);
    } catch (e) { 
      console.error("Firebase init error:", e); 
      setError("Failed to initialize the application."); 
      setLoading(false); 
    }
  }, []);

  useEffect(() => {
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const tokenResult = await currentUser.getIdTokenResult();
        const userIsAdmin = tokenResult.claims.admin === true;

        const userDocPath = `artifacts/${appId}/public/data/users/${currentUser.uid}`;
        const userDoc = await getDoc(doc(db, userDocPath));
        if (userDoc.exists()) {
            setUserData({ ...userDoc.data(), isAdmin: userIsAdmin, id: currentUser.uid });
        } else {
            // This might happen during registration flow
            setUserData({ isAdmin: userIsAdmin, id: currentUser.uid });
        }

      } else {
        try {
          if (typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
            await signInWithCustomToken(auth, window.__initial_auth_token);
          } else {
            await signInAnonymously(auth);
          }
                  } catch (authError) {
          if (authError.code === 'auth/custom-token-mismatch') {
             console.warn("Custom token mismatch, falling back to anonymous sign-in.");
          } else {
             console.error("Custom token sign-in failed:", authError);
          }

          try {
            await signInAnonymously(auth);
          } catch (anonError) {
             console.error("Anonymous fallback sign-in also failed:", anonError);
             setError("Could not initialize user session.");
          }
        }
      }
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, [auth, db, appId]);

  // --- Updated: Main Data Listeners ---
  useEffect(() => {
    if (!isAuthReady || !db || !user) {
        return;
    }
    
    setLoading(true);

    const challengesUnsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/challenges`), (snapshot) => {
      setChallenges(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.order - b.order));
    }, (err) => { console.error("Error fetching challenges:", err); setError("Could not load challenges."); });

    const bonusActionsUnsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/bonusActions`), (snapshot) => {
      setBonusActions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => { console.error("Error fetching bonus actions:", err);});

    const q = query(collection(db, `artifacts/${appId}/public/data/users`), where("leaderboardOptIn", "==", true));
    const leaderboardUnsub = onSnapshot(q, (snapshot) => {
      const sortedLeaderboard = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => {
          if ((b.totalPoints || 0) !== (a.totalPoints || 0)) {
              return (b.totalPoints || 0) - (a.totalPoints || 0);
          }
          return (a.lastScoreTimestamp?.toMillis() || 0) - (b.lastScoreTimestamp?.toMillis() || 0);
      });
      setLeaderboard(sortedLeaderboard);
    }, (err) => console.error("Error fetching leaderboard:", err));

    const usersUnsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/users`), (snapshot) => {
        setAllUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => console.error("Error fetching all users:", err));
        
    const broadcastsQuery = query(collection(db, `artifacts/${appId}/public/data/broadcasts`), orderBy("createdAt", "desc"));
    const broadcastsUnsub = onSnapshot(broadcastsQuery, (snapshot) => {
        setBroadcasts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, err => console.error("Error fetching broadcasts", err));
    
    // --- START MODIFICATIONS FOR PUBLIC BREAKDOWN ---
    
    // 1. New State: Public Challenge Score Breakdowns (assuming a server process populates this)
    // This public collection replaces the need for clients to read private challengeAttempts.
    let publicChallengeBreakdownUnsub = () => {};
    publicChallengeBreakdownUnsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/publicChallengeBreakdowns`), 
        s => setAllChallengeSubmissions(s.docs.map(d => ({ id: d.id, ...d.data() }))), 
        e => console.error("Public challenge breakdown fetch error:", e)
    );

    // 2. Public Bonus Submissions (Used by everyone)
    const allBonusSubmissionsUnsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/bonusSubmissions`), 
        s => setAllBonusSubmissions(s.docs.map(d => ({ id: d.id, ...d.data() }))), 
        e => console.error("Admin bonus submissions fetch error:", e)
    );
    
    let userUnsub = () => {};
    let submissionsUnsub = () => {};
    let bonusSubmissionsUnsub = () => {};

    if (user) {
      if (!user.isAnonymous) {
        const userDocPath = `artifacts/${appId}/public/data/users/${user.uid}`;
        userUnsub = onSnapshot(doc(db, userDocPath), (docSnap) => {
          const currentData = docSnap.exists() ? docSnap.data() : null;
          setUserData(currentData);
          if (currentData) {
            const earnedBadges = currentData.badges || [];
            const viewedBadges = currentData.viewedBadges || [];
            const newUnviewedBadges = earnedBadges.some(badgeId => !viewedBadges.includes(badgeId));
            setHasUnviewedBadges(newUnviewedBadges);
          }
          setLoading(false); 
        }, (err) => { console.error("Error fetching user data:", err); setLoading(false); });

        submissionsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${user.uid}/submissions`), (snapshot) => {
            const subs = {}; snapshot.forEach(doc => { subs[doc.id] = { id: doc.id, ...doc.data() }; });
            setSubmissions(subs);
        }, (err) => console.error("Error fetching submissions:", err));
        
        bonusSubmissionsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${user.uid}/bonusSubmissions`), (snapshot) => {
            const subs = {}; snapshot.forEach(doc => { subs[doc.id] = { id: doc.id, ...doc.data() }; });
            setBonusSubmissions(subs);
        }, (err) => console.error("Error fetching bonus submissions:", err));
        
      } else {
        setUserData(null);
        setSubmissions({});
        setBonusSubmissions({});
        setLoading(false);
      }
    } else {
       setLoading(false);
    }
    
    return () => { 
        userUnsub(); 
        submissionsUnsub(); 
        bonusSubmissionsUnsub(); 
        challengesUnsub(); 
        leaderboardUnsub(); 
        usersUnsub(); 
        broadcastsUnsub();
        publicChallengeBreakdownUnsub();
        allBonusSubmissionsUnsub(); // <-- Make sure to detach this listener
    };
    // --- END MODIFICATIONS FOR PUBLIC BREAKDOWN ---
  }, [isAuthReady, db, user, appId]);

  // --- Updated: Admin-specific listener ---
  useEffect(() => {
    // This array will hold our unsubscribe functions
    const unsubs = [];
    let allChallengeSubmissionsUnsub = () => {};
    let allLegacySubmissionsUnsub = () => {};

    // This state will hold data from both sources
    const combinedSubmissions = {};

    const updateState = () => {
        // Flatten the map of maps into a single array
        const allSubs = Object.values(combinedSubmissions).flatMap(userMap => Object.values(userMap));
        setAllChallengeSubmissions(allSubs);
    };

    if (userData?.isAdmin && db) {
        // Admin needs to get ALL challenge submissions, new and legacy.
        
        // Listener for 'challengeAttempts'
        allChallengeSubmissionsUnsub = onSnapshot(query(collectionGroup(db, 'challengeAttempts')), 
            (snapshot) => {
                snapshot.docChanges().forEach(change => {
                    const doc = change.doc;
                    const data = doc.data();
                    const userId = doc.ref.parent.parent.id;
                    if (!userId) return;

                    if (change.type === "removed") {
                        if (combinedSubmissions[userId] && combinedSubmissions[userId][doc.id]) {
                            delete combinedSubmissions[userId][doc.id];
                        }
                    } else {
                        if (!combinedSubmissions[userId]) combinedSubmissions[userId] = {};
                        combinedSubmissions[userId][doc.id] = { 
                            id: doc.id, 
                            ...data, 
                            userId: userId,
                            // Ensure score is normalized
                            scoreAwarded: data.scoreAwarded !== undefined ? data.scoreAwarded : (data.score || 0)
                        };
                    }
                });
                updateState(); // Update state with merged data
            }, 
            e => {
                if (e.code !== 'permission-denied') {
                     console.error("Admin collectionGroup challenge submissions fetch error:", e)
                }
            }
        );
        unsubs.push(allChallengeSubmissionsUnsub);


        // *** FIX 2: ADDED LISTENER FOR 'submissions' ***
        allLegacySubmissionsUnsub = onSnapshot(query(collectionGroup(db, 'submissions')),
            (snapshot) => {
                snapshot.docChanges().forEach(change => {
                    const doc = change.doc;
                    const data = doc.data();
                    // Path is /users/{userId}/submissions/{challengeId}
                    const userId = doc.ref.parent.parent.id;
                    const challengeId = doc.id; // Doc ID is the challenge ID
                    if (!userId || !challengeId) return;

                    if (change.type === "removed") {
                         if (combinedSubmissions[userId] && combinedSubmissions[userId][doc.id]) {
                            delete combinedSubmissions[userId][doc.id];
                        }
                    } else {
                        if (!combinedSubmissions[userId]) combinedSubmissions[userId] = {};
                        // Use doc.id (challengeId) as the unique key
                        combinedSubmissions[userId][doc.id] = {
                            id: doc.id,
                            ...data,
                            userId: userId,
                            challengeId: challengeId, // Explicitly set challengeId
                            // Map legacy 'score' to 'scoreAwarded' for consistency in the audit modal
                            scoreAwarded: data.score !== undefined ? data.score : 0 
                        };
                    }
                });
                updateState(); // Update state with merged data
            },
            e => {
                 if (e.code !== 'permission-denied') {
                     console.error("Admin collectionGroup legacy submissions fetch error:", e)
                }
            }
        );
        unsubs.push(allLegacySubmissionsUnsub);

    } 
    // IMPORTANT: If not admin, the public listener from the main useEffect is used, 
    // so no need for an else block here.
    
    // Return a cleanup function that unsubscribes from all listeners
    return () => { 
        unsubs.forEach(unsub => unsub());
    };
    
  }, [userData?.isAdmin, db]); // Depends only on admin status



  useEffect(() => {
    if (userData && bonusActions.length > 0) {
        const viewedActions = userData.viewedBonusActions || [];
        const hasNewActions = bonusActions.some(action => !viewedActions.includes(action.id));

        const approvedSubmissions = Object.values(bonusSubmissions).filter(s => s.status === 'Approved');
        const acknowledged = userData.acknowledgedApprovals || [];
        const hasNewApprovals = approvedSubmissions.some(sub => sub.publicSubmissionId && !acknowledged.includes(sub.publicSubmissionId));
        
        setHasUnviewedBonusContent(hasNewActions || hasNewApprovals);
    }
  }, [userData, bonusActions, bonusSubmissions]);


  const callGetHintFunction = async (systemPrompt, userQuery) => {
    if (!functions) {
        console.error("Firebase Functions not initialized.");
        return "Sorry, the hint service is not currently available.";
    }

    const getHint = httpsCallable(functions, 'getHint');
    
    try {
        const result = await getHint({ systemPrompt, userQuery });
        if (result.data && result.data.text) {
            return result.data.text;
        } else {
            console.error("Unexpected response structure from getHint function:", result.data);
            return "The hint service returned an unexpected response.";
        }
    } catch (error) {
        console.error("Error calling 'getHint' function:", error.code, error.message);
        if (error.code === 'unavailable') {
             return "The hint service is currently unavailable. Please try again later.";
        }
        if (error.code === 'functions/internal') {
             return "The hint service encountered an internal error and couldn't generate a hint. This might be a temporary issue. Please try again in a few moments.";
        }
        return "Sorry, there was an error connecting to the hint service.";
    }
  };


  const handleRegister = async (email, password, displayName, avatar, leaderboardOptIn, emailOptIn) => {
    if (!auth || !db) return false;
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      const userDocPath = `artifacts/${appId}/public/data/users/${newUser.uid}`;
      await setDoc(doc(db, userDocPath), { 
        email: newUser.email, 
        displayName, 
        avatar, 
        leaderboardOptIn, 
        emailOptIn,
        totalPoints: leaderboardOptIn ? 10 : 0, 
        createdAt: serverTimestamp(),
        lastScoreTimestamp: null, 
        badges: [],
        viewedBadges: [],
        viewedBonusActions: [],
        acknowledgedApprovals: [],
        pointsBreakdown: { 
            base: leaderboardOptIn ? 10 : 0, 
            challenges: 0, 
            bonus: 0 
        },
      });
      if (leaderboardOptIn) {
        showMessage('Registration successful! You have been awarded 10 points for joining the leaderboard. Welcome!', 'success');
      } else {
        showMessage('Registration successful! Welcome!', 'success');
      }
      return true;
    } catch (error) {
        console.error("Registration error:", error);
        let friendlyMessage = "Registration failed. Please try again.";
        if (error.code === 'auth/email-already-in-use') {
            friendlyMessage = "This email is already registered. Please log in instead.";
        } else if (error.code === 'auth/weak-password') {
            friendlyMessage = "Password is too weak. It should be at least 6 characters long.";
        }
        setError(friendlyMessage);
        return false;
    }
  };

  const handleLogin = async (email, password) => {
    if (!auth) return false;
    try {
      setError('');
      await signInWithEmailAndPassword(auth, email, password);
      showMessage('Welcome back!', 'success');
      return true;
    } catch (error) {
        console.warn("Login attempt failed:", error.code);
        let friendlyMessage = "Login failed. Please try again.";
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/invalid-email') {
            friendlyMessage = "Invalid email or password. Please check your credentials and try again.";
        }
        setError(friendlyMessage);
        return false;
    }
  };

  const handleLogout = async () => {
    if (!auth) return;
    await signOut(auth);
    setUserData(null);
    showMessage('You have been logged out.', 'info');
  };

  const handleSelectChallenge = (challengeId) => {
    setSelectedChallengeId(challengeId); setPage('challenge');
  };
  
  const handleAuthModalOpen = (view = 'register') => {
    setError('');
    setInitialModalView(view);
    setShowJoinModal(true);
  };
  
  const markAllBadgesAsViewed = async () => {
    if (user && !user.isAnonymous && userData.badges) {
      const userRef = doc(db, `artifacts/${appId}/public/data/users`, user.uid);
      await updateDoc(userRef, {
        viewedBadges: userData.badges,
      });
      setShowNewBadgeModal(false); // Close modal
    }
  };
  
  const markBadgeAsViewed = async (badgeId) => {
    if (user && !user.isAnonymous && !(userData.viewedBadges || []).includes(badgeId)) {
        const userRef = doc(db, `artifacts/${appId}/public/data/users`, user.uid);
        await updateDoc(userRef, {
            viewedBadges: arrayUnion(badgeId)
        });
    }
  };


  const handleShowBadgeModal = (imageUrl, title, badgeId) => {
    setModalBadge({ imageUrl, title });
    markBadgeAsViewed(badgeId);
  };

  const handleSubmitAnswer = async (challenge, answer, oldScore = 0) => {
    if (!db || !user) {
        showMessage('An error occurred. Please refresh the page.', 'error');
        return;
    }

    // Handle anonymous user submission locally (no secure function call)
    if (user.isAnonymous) {
        let totalScore = 0;
        let isCorrect = false;
        const v = challenge.validation;
        
        // ... (performing simple local validation for anonymous users) ...
        if (challenge.answerType === 'simple' || challenge.answerType === 'multi_part_text') {
             // Cannot securely grade AI parts or complex logic
             isCorrect = false; 
             totalScore = 0;
        }
        // ... (other simple types) ...

        if (totalScore > 0) {
            showMessage(`You earned ${totalScore} points! Register now to save your score and join the leaderboard.`, 'info');
            handleAuthModalOpen('register');
        } else {
            showMessage("That wasn't quite right. Try looking at it from a different angle!", 'error');
        }
        return { score: totalScore, isCorrect, partFeedback: {}, answer };
    }

    // Handle registered user submission (SECURE FLOW)
    if (!userData) {
      showMessage('User data is still loading, please try again in a moment.', 'info');
      return;
    }

    if (!functions) {
      showMessage('Cannot connect to challenge server. Please try again.', 'error');
      return;
    }

    try {
      const gradeChallengeAnswer = httpsCallable(functions, 'gradeChallengeAnswer');
      const result = await gradeChallengeAnswer({ 
        challengeId: challenge.id, 
        answer: answer 
      });

      const { success, message, isCorrect, badgeAwarded } = result.data;
      
      showMessage(message, success ? (isCorrect ? 'success' : 'info') : 'error');
      
      if (badgeAwarded) {
        setShowNewBadgeModal(true);
      }

    } catch (error) { 
      console.error("Error calling gradeChallengeAnswer function:", error); 
      showMessage(error.message || 'Error submitting answer.', 'error'); 
    }
    
    return null; // DB write is now handled by the server
  };
  
  const handleSubmitBonusAction = async (action, answer) => {
    if (!db || !user || user.isAnonymous || !userData) {
      showMessage('You must be logged in to submit.', 'error');
      if (user?.isAnonymous) handleAuthModalOpen('register');
      return;
    }

    const existingSubmission = Object.values(bonusSubmissions).find(s => s.actionId === action.id);
    if (existingSubmission && (existingSubmission.status === 'Pending' || existingSubmission.status === 'Approved')) {
        showMessage('You already have a submission for this action.', 'info');
        return;
    }

    const now = serverTimestamp();
    try {
        const publicSubmissionData = {
            actionId: action.id, actionName: action.name, userId: user.uid, userDisplayName: userData.displayName, userAvatar: userData.avatar,
            answer, status: 'Pending', submittedAt: now,
        };
        const publicSubmissionRef = await addDoc(collection(db, `artifacts/${appId}/public/data/bonusSubmissions`), publicSubmissionData);

        const privateSubmissionData = {
            actionId: action.id, // Storing actionId here too
            answer, status: 'Pending', submittedAt: now, publicSubmissionId: publicSubmissionRef.id,
        };
        await setDoc(doc(db, `artifacts/${appId}/users/${user.uid}/bonusSubmissions`, action.id), privateSubmissionData);
        
        showMessage('Your submission has been received and is pending review!', 'success');
    } catch (error) {
        console.error("Error submitting manual bonus action:", error);
        showMessage('There was an error submitting your action. Please try again.', 'error');
    }
  };

  const currentChallenge = useMemo(() => challenges.find(c => c.id === selectedChallengeId), [selectedChallengeId, challenges]);
  const navigate = (targetPage) => { 
    if (targetPage === 'rewards' && hasUnviewedBadges) {
        setShowNewBadgeModal(true);
    }
    setError(''); 
    setPage(targetPage); 
  };

  const maxChallengePoints = useMemo(() => challenges.reduce((sum, c) => sum + (Number(c.points) || 0), 0), [challenges]);
  const maxBonusPoints = useMemo(() => bonusActions.reduce((sum, a) => sum + (Number(a.points) || 0) + (Number(a.extraPoints) || 0), 0), [bonusActions]);
  const maxBasePoints = 10; // For joining the leaderboard

  if (loading && !isAuthReady) { 
    return <div style={{backgroundColor: brandColors.federalBlue}} className="text-white min-h-screen flex items-center justify-center"><Spinner /></div>;
  }
  
  const renderPage = () => {
    switch (page) {
      case 'challenge': return currentChallenge ? <ChallengePage challenge={currentChallenge} submissions={submissions} onSubmit={handleSubmitAnswer} onBack={() => navigate('dashboard')} onImageZoom={setZoomedImageUrl} callGetHint={callGetHintFunction} db={db} user={user} appId={appId} userData={userData}/> : <p>Challenge not found.</p>;
      case 'leaderboard': return <LeaderboardPage 
            leaderboardData={leaderboard} 
            currentUserData={userData} 
            maxChallengePoints={maxChallengePoints} 
            maxBonusPoints={maxBonusPoints} 
            maxBasePoints={maxBasePoints}
            allChallengeSubmissionsByUser={allChallengeSubmissionsByUser}
            allBonusSubmissionsByUser={allBonusSubmissionsByUser}
            challenges={challenges}
        />;
      case 'rewards': return <RewardsPage userData={userData} challenges={challenges} onShowBadge={handleShowBadgeModal} />;
      case 'bonus': 
        return <BonusPointsPage 
            user={user} 
            userData={userData}
            bonusActions={bonusActions} 
            bonusSubmissions={Object.values(bonusSubmissions)}
            allBonusSubmissions={allBonusSubmissions}
            onSubmit={handleSubmitBonusAction}
            showNotification={showMessage}
        />;
      case 'profile': return <ProfilePage userData={userData} avatars={avatars} db={db} user={user} appId={appId} showNotification={showMessage} onNavigate={navigate} />;
      case 'privacy': return <PrivacyPolicyPage />;
      case 'admin': 
        if (!userData?.isAdmin) return <p>Access Denied</p>;
        return <AdminPanel 
            allUsers={allUsers} 
            challenges={challenges} 
            bonusActions={bonusActions} 
            db={db} 
            appId={appId} 
            showNotification={showMessage} 
            user={user} 
            userData={userData}
            allBonusSubmissions={allBonusSubmissions}
            pendingSubmissions={pendingBonusSubmissions}
            onReview={handleReviewBonusSubmission}
            functions={functions}
            allChallengeSubmissions={allChallengeSubmissions}
            onAdminPasswordReset={handleAdminPasswordReset}
            onUpdateUserEmail={handleUpdateUserEmail}
            // CRITICAL: Pass the challenge submissions (attempts) for the audit modal
            allChallengeSubmissionsByUser={allChallengeSubmissionsByUser} 
          />;
      default:
        return <HomePage user={user} userData={userData} challenges={challenges} onSelectChallenge={handleSelectChallenge} loading={loading} broadcasts={broadcasts} allUsersCount={allUsers.length} onAuthModalOpen={handleAuthModalOpen} onNavigate={navigate} showMessage={showMessage} />;
    }
  };

  return (
    <div style={{backgroundColor: brandColors.federalBlue, color: brandColors.isabelline}} className="min-h-screen font-sans">
      {/* --- THIS LINE CAUSED THE ERROR AND IS NOW REMOVED --- */}
      {/* <style>{marqueeStyles}</style> */}
      {messageModal.message && <MessageModal message={messageModal.message} type={messageModal.type} onClose={() => setMessageModal({ message: '', type: ''})} />}
      {zoomedImageUrl && <ImageZoomModal imageUrl={zoomedImageUrl} onClose={() => setZoomedImageUrl(null)} />}
      {showNewBadgeModal && <NewBadgeModal onClose={markAllBadgesAsViewed} />}
      <Header user={user} userData={userData} onLogout={handleLogout} onNavigate={navigate} onJoin={handleAuthModalOpen} hasUnviewedBadges={hasUnviewedBadges} hasUnviewedBonusContent={hasUnviewedBonusContent} />
      <main className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
        {error && <div className="bg-red-500/20 text-red-300 p-4 rounded-lg mb-4 text-center">{error}</div>}
        {renderPage()}
      </main>
      <Footer onNavigate={navigate} />
      {showJoinModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
               <div className="relative max-w-xl w-full">
                  <button onClick={() => setShowJoinModal(false)} className="absolute -top-4 -right-4 bg-gray-800 rounded-full p-2 text-white hover:bg-gray-700 z-10">
                    <XIcon/>
                  </button>
                <JoinLoginModule 
                    onLogin={handleLogin} 
                    onRegister={handleRegister} 
                    onForgotPassword={handleForgotPassword}
                    avatars={avatars} 
                    error={error} 
                    setError={setError} 
                    onSuccess={() => setShowJoinModal(false)}
                    initialView={initialModalView}
                />
              </div>
          </div>
      )}
      {modalBadge && <BadgeModal imageUrl={modalBadge.imageUrl} title={modalBadge.title} onClose={() => setModalBadge(null)} />}
    </div>
  );
}

